// src/pages/shop/LiveShopCreation.js - Mobile Optimized

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import TreasureMap from '../../components/map/TreasureMap';
import ValidatedEditableText from '../../components/common/ValidatedEditableText';
import logo from './logo512.png';
import { VALIDATION_RULES, validateShopData, validateAllItems } from '../../utils/inputValidation';
import { SHOP_OPENING_FEE, PLANS, PLAN_ORDER, PLAN_PERMISSIONS } from '../../utils/billingTiers';
import { validateImageFile, compressProductImage } from '../../utils/mediaCompression';
import { 
  ChevronLeft, 
  ChevronRight, 
  Settings, 
  Plus,
  Film, 
  Camera,
  MessageCircle,
  Users,
  RefreshCw, 
  Pin,
  X,
  ChevronDown,
  ChevronUp,
  Store,
  Home,
  LogOut,
  Check,
  Package,
  Instagram,
  Twitter, 
  Facebook,
  Mail,
  Phone, 
  MapPin
} from 'lucide-react';
import { useTempStore } from '../../contexts/TempStoreContext';
import EditableText from './components/EditableComponents/EditableText';
import EditableImage from './components/EditableComponents/EditableImage';
import EditableItem from './components/EditableComponents/EditableItem';
import TabPositioner from './components/TabPositioner';
import { DEFAULT_THEME } from '../../theme/config/themes';
import ThemeSelector from '../../components/ThemeSelector/ThemeSelector';
import AddressInput from '../../components/shop/AddressInput';
import { WELCOME_STYLES } from '../../theme/welcomeStyles';
import QuantitySelector from '../../components/shop/QuantitySelector';
import { useAuth } from '../../contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth, safeSignInWithPopup, googleProvider } from '../../firebase/config';
import { saveShopData, checkExistingShop } from '../../firebase/firebaseService';
import {
  StreetwearTemplate,
  OrganizationTemplate,
  TechTemplate,
  MinimalistTemplate,
  LocalMarketTemplate
} from './HomePageTemplate';

import { generateUsername } from '../../firebase/firebaseService';
import { STORE_TYPES } from '../../constants/categories';





const ITEM_CATEGORIES = [
  'Electronics & Tech',
  'Clothing & Accessories',
  'Home & Garden',
  'Sports & Outdoors',
  'Books & Media',
  'Toys & Games',
  'Health & Beauty',
  'Automotive',
  'Collectibles & Art',
  'Food & Beverages',
  'Entertainment',
  'Miscellaneous',
  'Other'
];

const RESTAURANT_CATEGORIES = STORE_TYPES.find(t => t.id === 'restaurant').categories;

const RestaurantToggle = styled.button`
  background: ${props => props.active ? 
    props.theme?.colors?.accent : 'transparent'};
  border: 2px solid ${props => props.theme?.colors?.accent};
  color: ${props => props.active ? 'white' : props.theme?.colors?.accent};
  padding: 0.5rem 1rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => `${props.theme?.colors?.accent}40`};
  }
  
  &:active {
    transform: translateY(0);
  }
`;


const SERVICE_CATEGORIES = [
  'Professional Services',
  'Home Services',
  'Personal Care',
  'Education & Tutoring',
  'Health & Wellness',
  'Creative Services',
  'Technology Services',
  'Automotive Services',
  'Event Services',
  'Consulting',
  'Other'
];

// UPDATE HeaderLeft to include logo
const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (min-width: 768px) {
    gap: 0.75rem;
  }
`;

// UPDATE HeaderRight to show theme controls on mobile
const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (min-width: 768px) {
    gap: 0.75rem;
  }
`;

// UPDATE the ItemsContainer styled component
const ItemsContainer = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: ${props => `${props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'}30`};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(128, 0, 0, 0.3)'};
  position: relative;
  z-index: 2;

  @media (min-width: 768px) {
    padding: 2rem;
  }
  
  /* ADD THIS - Remove styling on mobile */
  @media (max-width: 767px) {
    background: transparent;
    backdrop-filter: none;
    border: none;
    padding: 0;
    margin-top: 1rem;
  }
`;

// UPDATE EditModal styled component (around line 350)
const EditModal = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    position: fixed;
    top: 85px; /* Match header height */
    left: 0;
    right: 0;
    bottom: 0; /* Changed from 30px to 0 to use full screen */
    background: rgba(0, 0, 0, 0.95); /* Darker background */
    align-items: center;
    justify-content: center;
    z-index: 999;
    padding: 0; /* Remove padding for full screen usage */
    overflow: hidden;
  }

  body {
    overflow: ${props => props.show ? 'hidden' : 'auto'};
    position: ${props => props.show ? 'fixed' : 'static'};
    width: 100%;
  }
`;

// UPDATE EditModalContent styled component (around line 370)
const EditModalContent = styled.div`
  background: ${props => props.theme?.colors?.background || '#000000'};
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 0; /* Changed from 12px for full screen */
  width: 100%;
  max-width: 90%; /* Changed from 400px */
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 0; /* Changed from auto */
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
`;

// UPDATE SaveButtonContainer to hide when editing (around line 1150)
const SaveButtonContainer = styled.div`
  position: fixed;
  bottom: 2.5rem;
  right: 1rem;
  z-index: 1000;
  
  /* Hide on mobile when editing */
  @media (max-width: 767px) {
    display: ${props => props.isHidden ? 'none' : 'block'};
  }
  
  @media (min-width: 768px) {
    bottom: 2rem;
    right: 2rem;
  }
`;

const EditModalScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 80px; /* Add padding for bottom button area */
  
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme?.colors?.accent || '#800000'};
    border-radius: 2px;
  }
`;

const ImageActionButtons = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 0.75rem;
  z-index: 10;
  background: ${props => `${props.theme?.colors?.background || '#000000'}F5`};
  backdrop-filter: blur(10px);
  padding: 0.75rem;
  justify-content: center;
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(255, 255, 255, 0.1)'};
`;

const ImageActionButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &.check {
    background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.8)'}E5`};
    color: #4CAF50;
    
    &:active {
      background: #4CAF50;
      color: white;
      transform: scale(0.95);
    }
  }
  
  &.close {
    background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.8)'}E5`};
    color: #ff4444;
    
    &:active {
      background: #ff4444;
      color: white;
      transform: scale(0.95);
    }
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const EditModalImageSection = styled.div`
  width: 100%;
  min-height: 300px;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(255, 255, 255, 0.1)'};
`;

const EditModalBody = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

// Add after existing styled components (around line 800)
const ShopNameInputContainer = styled.div`
  width: 100%;
  margin: 0.5rem 0;
  position: relative;
`;

const ShopNameInput = styled.input`
  width: 100%;
  text-align: center;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.isError ? '#ff4444' : `${props.theme?.colors?.accent}40` || 'rgba(128, 0, 0, 0.25)'};
  font-size: ${props => Math.min(props.fontSize || 2.5, 2)}rem;
  font-family: ${props => props.theme?.fonts?.heading};
  color: ${props => props.theme?.colors?.accent || '#FF0000'};
  outline: none;
  padding: 0.5rem;
  transition: all 0.3s ease;
  caret-color: ${props => props.theme?.colors?.accent || '#800000'};
  font-weight: 900;
  letter-spacing: 0.5px;
  
  @media (min-width: 768px) {
    font-size: ${props => props.fontSize || '2.5rem'};
    letter-spacing: 1px;
  }

  &:focus {
    border-bottom-color: ${props => props.isError ? '#ff4444' : props.theme?.colors?.accent || '#800000'};
    border-bottom-width: 3px;
    text-shadow: 0 0 10px ${props => `${props.theme?.colors?.accent}40`};
  }

  &::placeholder {
    color: ${props => `${props.theme?.colors?.accent}FF` || 'rgba(255, 0, 0, 1)'}; // Changed from 80 to FF (100% opacity)
    font-weight: 1800; // ADD THIS - makes placeholder bolder
  }

  &:focus {
    animation: blink 1s step-end infinite;
  }
`;

const ShopNameError = styled.div`
  color: #ff4444;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  text-align: center;
  font-family: ${props => props.theme?.fonts?.body || "'Inter', sans-serif"};
  min-height: 20px;
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ShopNameSuccess = styled.div`
  color: #4CAF50;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  text-align: center;
  font-family: ${props => props.theme?.fonts?.body || "'Inter', sans-serif"};
  min-height: 20px;
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;


const HeaderCenter = styled.div`
  display: none;
  
  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    gap: 1rem;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
`;


// CHANGE 3: Update ItemCard styled component (around line 346)
// REPLACE the entire ItemCard component with:
const ItemCard = styled.div`
  background: ${props => props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'};
  border-radius: ${props => props.theme?.styles?.borderRadius || '12px'};
  overflow: hidden;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(255, 255, 255, 0.1)'};
  position: relative;
  transition: all 0.3s;
  width: 100%;
  min-width: 320px;

  &:hover {
    transform: translateY(-5px);
    border-color: ${props => props.theme?.colors?.accent};
  }
`;

const placeholderImages = [
    'https://images.unsplash.com/photo-1767016311533-8365d673f91b?q=80&w=1318&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', // Earth from space - global reach
  'https://images.unsplash.com/photo-1472214103451-9374bd1c798e?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', // Mountain peak success - reaching new heights
  'https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?q=80&w=1144&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'];

const ItemImageContainer = styled.div`
  position: relative;
  height: 250px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => `${props.theme?.colors?.background || '#000000'}50`};

  @media (min-width: 768px) {
    height: 280px;
  }

  .image-container {
    width: 100%;
    height: 100%;
    position: relative;
    touch-action: pan-y;
  }

  .placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: ${props => props.theme?.colors?.text || '#fff'};
    opacity: 0.5;
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 1rem;
    
    &:hover {
      opacity: 0.8;
      background: ${props => `${props.theme?.colors?.accent}10` || 'rgba(255, 255, 255, 0.05)'};
      
      /* Scale up the circle on hover */
      > div:first-child {
        transform: scale(1.1);
        background: ${props => `${props.theme?.colors?.accent}25`};
      }
    }
    
    &:active {
      opacity: 1;
      
      > div:first-child {
        transform: scale(0.95);
      }
    }
    
    span {
      font-size: 0.9rem;
      font-family: ${props => props.theme?.fonts?.body || "'Inter', sans-serif"};
    }
  }

  .carousel-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: ${props => `${props.theme?.colors?.background || '#000000'}80`};
    border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(255, 255, 255, 0.2)'};
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme?.colors?.text || 'white'};
    cursor: pointer;
    z-index: 2;
    opacity: 0.8;
    transition: all 0.3s ease;

    @media (max-width: 767px) {
      width: 28px;
      height: 28px;
      opacity: 0.9;
      
      &:active {
        transform: translateY(-50%) scale(0.9);
      }
    }

    &:hover {
      background: ${props => `${props.theme?.colors?.accent}40` || 'rgba(0, 0, 0, 0.7)'};
      opacity: 1;
    }

    &.left {
      left: 10px;
    }

    &.right {
      right: 10px;
    }
  }

  .image-dots {
    position: absolute;
    bottom: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 0.25rem;
    z-index: 2;
    
    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${props => props.theme?.colors?.text || '#FFFFFF'};
      opacity: 0.3;
      transition: opacity 0.3s ease;
      
      &.active {
        opacity: 1;
      }
    }
  }
`;


const FadingUnderline = styled.div`
  width: 100%;
  height: 2px;
  background: linear-gradient(
    to right,
    transparent 0%,
    ${props => props.theme?.colors?.accent || '#fff'} 20%,
    ${props => props.theme?.colors?.accent || '#fff'} 80%,
    transparent 100%
  );
  margin-top: 0.05rem;
  margin-bottom: 1rem;
  opacity: 0.6;
`;

// REPLACE the ItemsGrid with this version that uses auto-fill instead of auto-fit
const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
  
  @media (min-width: 480px) {
    gap: 1.5rem;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
  
  @media (min-width: 768px) {
    gap: 2rem;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  }

  /* Mobile template card grid - 2 items per row */
  @media (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    padding: 0 0.5rem;
  }
`;

// UPDATE the DesktopItemCard to constrain the width
const DesktopItemCard = styled(ItemCard)`
  @media (max-width: 767px) {
    display: none;
  }
  
  /* Constrain the width so items don't stretch too much */
  max-width: 400px;
  justify-self: center;
  
  /* Ensure each card maintains its own height */
  height: fit-content;
  min-height: 400px;
  transition: all 0.3s ease;
  
  /* When expanded, allow natural height but maintain grid behavior */
  ${props => props.expanded && `
    min-height: 600px;
    height: auto;
  `}
  
  /* Make the card take full width of its grid cell on mobile */
  @media (max-width: 767px) {
    max-width: none;
    justify-self: stretch;
  }
`;

// UPDATE the ItemDetails to use max-height instead of height for smoother transitions
const ItemDetails = styled.div`
  max-height: ${props => props.expanded ? '500px' : '0'};
  opacity: ${props => props.expanded ? '1' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  
  .details-content {
    padding-top: ${props => props.expanded ? '1rem' : '0'};
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    transition: all 0.3s ease;
  }
`;

const ItemContent = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  
  @media (min-width: 768px) {
    padding: 1.5rem;
    gap: 1rem;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  cursor: pointer;
  
  h4 {
    margin: 0;
    font-size: 1rem;
    color: ${props => props.theme?.colors?.text || '#FFFFFF'};
    
    @media (min-width: 768px) {
      font-size: 1.1rem;
    }
  }
`;

const ExpandButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:active {
    transform: scale(0.9);
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  z-index: 10;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
    background: rgba(255, 0, 0, 0.5);
  }
`;


const CategorySelect = styled.select`
  width: 100%;
  background: #1a1a1a !important;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${props => props.theme?.styles?.borderRadius || '8px'};
  padding: 0.75rem;
  color: #ffffff !important;
  font-family: ${props => props.theme?.fonts?.body || "'Inter', sans-serif"};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.accent || '#800000'};
  }
  
  option {
    background: #1a1a1a !important;
    color: #ffffff !important;
    padding: 0.5rem;
  }
`;


const ActionButton = styled.button`
  background: ${props => props.theme?.colors?.accent || '#800000'};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 30px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: ${props => props.theme?.fonts?.body || 'sans-serif'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

  @media (max-width: 767px) {
    padding: 0.8rem 1.5rem;
    font-size: 0.9rem;
    letter-spacing: 0.5px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${props => `${props.theme?.colors?.accent}4D` || 'rgba(128, 0, 0, 0.3)'};
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;


const GlobalStyles = createGlobalStyle`
  .ping {
    position: absolute;
    width: 2px;
    height: 2px;
    border-radius: 50%;
    background: #800000;
    pointer-events: none;
    
    /* Disable on mobile for performance */
    @media (max-width: 767px) {
      display: none;
    }
  }

  .ping::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 200px;
    height: 200px;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: radial-gradient(circle, rgba(128, 0, 0, 0.4) 0%, transparent 70%);
    animation: ping 2s ease-out forwards;
  }

  @keyframes ping {
    0% {
      width: 0px;
      height: 0px;
      opacity: 1;
    }
    100% {
      width: 200px;
      height: 200px;
      opacity: 0;
    }
  }
`;


// ADD this near the top of the LiveShopCreation component if not already present
const SectionWrapper = ({ children, theme, noPadding = false, delay = 0 }) => {
  return (
    <div style={{
      position: 'relative',
      padding: noPadding ? '0' : 'clamp(1rem, 2vw, 2rem) clamp(0.5rem, 1vw, 1rem)',
      background: 'transparent',
      animation: 'fadeInUp 0.6s ease-out forwards',
      animationDelay: `${delay}s`,
      opacity: 0
    }}>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      {children}
    </div>
  );
};

const MobileTemplateImageContainer = styled.div`
  position: relative;
  height: 120px;
  overflow: hidden;
  background: ${props => `${props.theme?.colors?.background || '#000000'}50`};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    color: ${props => props.theme?.colors?.text || '#fff'};
    opacity: 0.3;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;

    svg {
      width: 24px;
      height: 24px;
    }

    span {
      font-size: 0.75rem; /* Changed from 0.7rem and made slightly larger */
      font-weight: 500; /* Added for better readability */
    }
  }

  .mobile-carousel-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.7)'}DD`};
    border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(255, 255, 255, 0.2)'};
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme?.colors?.text || 'white'};
    cursor: pointer;
    z-index: 3;
    opacity: 0.8;
    transition: all 0.2s ease;

    &:active {
      opacity: 1;
      transform: translateY(-50%) scale(0.9);
      background: ${props => props.theme?.colors?.accent || '#800000'};
    }

    &.left {
      left: 0.25rem;
    }

    &.right {
      right: 0.25rem;
    }

    svg {
      width: 12px;
      height: 12px;
    }
  }

  .image-dots {
    position: absolute;
    bottom: 0.25rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 0.2rem;
    z-index: 2;
    
    .dot {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: ${props => props.theme?.colors?.text || '#FFFFFF'};
      opacity: 0.3;
      transition: opacity 0.3s ease;
      
      &.active {
        opacity: 1;
      }
    }
  }
`;

// Mobile Template Card (only shows on mobile)
const MobileTemplateCard = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    background: ${props => props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'};
    border-radius: ${props => props.theme?.styles?.borderRadius || '12px'};
    overflow: hidden;
    border: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(255, 255, 255, 0.1)'};
    position: relative;
    transition: all 0.3s ease;
    cursor: pointer;
    min-height: 200px;

    &:active {
      transform: scale(0.98);
      border-color: ${props => props.theme?.colors?.accent};
    }
  }
`;

const MobileTemplateContent = styled.div`
  padding: 0.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .item-name {
    font-size: 0.75rem;
    font-weight: 600;
    color: ${props => props.theme?.colors?.text || '#FFFFFF'};
    margin-bottom: 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
  }

  .item-price {
    font-size: 0.85rem;
    font-weight: bold;
    color: ${props => props.theme?.colors?.accent || '#800000'};
    text-align: right;
  }

  .empty-text {
    font-size: 0.7rem;
    color: ${props => `${props.theme?.colors?.text}60` || 'rgba(255, 255, 255, 0.4)'};
    font-style: italic;
  }
`;

const MobileDeleteButton = styled.button`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.7)'}DD`};
  border: 1px solid ${props => `${props.theme?.colors?.accent}60` || 'rgba(255, 255, 255, 0.3)'};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff4444;
  cursor: pointer;
  z-index: 4;
  opacity: 0.9;
  transition: all 0.2s ease;

  &:active {
    transform: scale(0.9);
    background: ${props => props.theme?.colors?.accent || '#800000'};
    color: white;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const PageContainer = styled.div.attrs({ className: 'page-container' })`
  min-height: 100vh;
  height: auto;
  background: ${props => props.theme?.colors?.background || '#000000'};
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  position: relative;
  overflow-x: hidden;
  
  /* Base gradient background */
  &::before {
    content: '';
    position: fixed;
    width: 100%;
    height: 100%;
    background: ${props => props.theme?.colors?.backgroundGradient || 'radial-gradient(circle at 20% 30%, rgba(128, 0, 0, 0.2) 0%, transparent 50%)'};
    opacity: 0.8;
    z-index: 0;
  }

  /* Realistic starry night - Layer 1: Small distant stars */
  &::after {
    content: '';
    position: fixed;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle, white 1px, transparent 1px),
      radial-gradient(circle, rgba(255, 255, 255, 0.8) 1px, transparent 1px);
    background-size: 150px 150px, 200px 200px;
    background-position: 0 0, 50px 50px;
    opacity: 0.4;
    z-index: 0;
    animation: twinkle 3s ease-in-out infinite;
  }

  @keyframes twinkle {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }

  @keyframes galaxySwirl {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// ADD new styled component for additional star layers
const StarField = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  
  /* Layer 2: Medium brightness stars */
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle, rgba(255, 255, 255, 0.9) 1.5px, transparent 1.5px),
      radial-gradient(circle, rgba(200, 220, 255, 0.7) 1px, transparent 1px);
    background-size: 250px 250px, 180px 180px;
    background-position: 25px 25px, 75px 75px;
    opacity: 0.5;
    animation: twinkleMedium 4s ease-in-out infinite;
  }
  
  /* Layer 3: Bright prominent stars */
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle, rgba(255, 255, 255, 1) 2px, transparent 2px),
      radial-gradient(circle, rgba(180, 200, 255, 0.9) 1.5px, transparent 1.5px);
    background-size: 400px 400px, 350px 350px;
    background-position: 100px 100px, 200px 200px;
    opacity: 0.7;
    animation: twinkleBright 2s ease-in-out infinite;
  }
  
  @keyframes twinkleMedium {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.7; }
  }
  
  @keyframes twinkleBright {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
`;

const ShootingStar = styled.div`
  position: absolute;
  width: 2px;
  height: 2px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 0 10px 2px rgba(255, 255, 255, 0.8);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 1px;
    background: linear-gradient(to right, rgba(255, 255, 255, 0.8), transparent);
  }
  
  &.shooting-star-1 {
    top: 20%;
    left: -100px;
    animation: shoot1 3s linear infinite;
  }
  
  &.shooting-star-2 {
    top: 40%;
    left: -100px;
    animation: shoot2 4s linear infinite 1.5s;
  }
  
  &.shooting-star-3 {
    top: 60%;
    left: -100px;
    animation: shoot3 5s linear infinite 3s;
  }
  
  @keyframes shoot1 {
    0% {
      transform: translateX(0) translateY(0);
      opacity: 1;
    }
    100% {
      transform: translateX(1000px) translateY(500px);
      opacity: 0;
    }
  }
  
  @keyframes shoot2 {
    0% {
      transform: translateX(0) translateY(0);
      opacity: 1;
    }
    100% {
      transform: translateX(1200px) translateY(600px);
      opacity: 0;
    }
  }
  
  @keyframes shoot3 {
    0% {
      transform: translateX(0) translateY(0);
      opacity: 1;
    }
    100% {
      transform: translateX(800px) translateY(400px);
      opacity: 0;
    }
  }
`;


const BillingExplainer = styled.div`
  background: ${props => `${props.theme?.colors?.surface}40`};
  backdrop-filter: blur(10px);
  border: 2px solid ${props => props.theme?.colors?.accent};
  border-radius: 12px;
  padding: 1.25rem;
  margin-top: 1.5rem;
  text-align: center;
  box-shadow: 0 4px 20px ${props => `${props.theme?.colors?.accent}20`};
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 6px 30px ${props => `${props.theme?.colors?.accent}30`};
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    width: 85%;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
    padding: 0.75rem;
    margin-top: 1rem;
    border-radius: 8px;
    border-width: 1.5px;
  }
`;

// CHANGE 2: Update BillingTitle for mobile
const BillingTitle = styled.h3`
  color: ${props => props.theme?.colors?.accent};
  font-size: 1.3rem;
  font-weight: 800;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-shadow: 0 0 10px ${props => `${props.theme?.colors?.accent}40`};
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin: 0 0 0.5rem 0;
    letter-spacing: 0.5px;
  }
`;

// CHANGE 3: Update GrowthMessage for mobile
const GrowthMessage = styled.p`
  color: ${props => props.theme?.colors?.text};
  font-size: 1rem;
  line-height: 1.8;
  margin: 0;
  font-weight: 500;
  
  strong {
    color: ${props => props.theme?.colors?.accent};
    font-weight: 700;
    font-size: 1.05rem;
  }
  
  br {
    display: block;
    content: "";
    margin-top: 0.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    line-height: 1.5;
    
    strong {
      font-size: 0.8rem;
      font-weight: 700;
    }
    
    br {
      margin-top: 0.25rem;
    }
  }
`;

// CHANGE 6: Add HeaderBottomRow component
const HeaderBottomRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0.5rem 0;
  border-top: 1px solid ${props => props.$shopTab ? 'transparent' : (`${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)')};

  @media (min-width: 768px) {
    display: none;
  }
`;

// UPDATE ShopProfileSection styled component (around line 240)
const ShopProfileSection = styled.section`
  /* Make section full-bleed (wall-to-wall) while keeping content centered */
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100vw;
  max-width: none;
  left: 50%;
  transform: translateX(-50%);
  margin: 0 0 2rem 0;
  padding: 0.75rem 1rem;
  background: ${props => `${props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'}30`};
  backdrop-filter: blur(6px);
  border-radius: 0;
  border: 1px solid ${props => `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)'};
  position: relative;
  z-index: 2;
  overflow: visible;
  /* Reserve more vertical space for profile image and avoid squishing */
  min-height: 44vh;
  padding-top: 2rem;

  @media (min-width: 768px) {
    padding: 2.5rem 2rem;
    min-height: 46vh;
  }

  .profile-image {
    margin-bottom: .5rem;
    margin-top: 0;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.06);
    border: 3px solid ${props => props.theme?.colors?.accent || '#800000'};
    box-shadow: 0 0 12px ${props => `${props.theme?.colors?.accent}30` || 'rgba(128, 0, 0, 0.15)'};
    position: relative;
    z-index: 3;
    flex: 0 0 auto; /* prevent shrinking inside flex container */
    align-self: center;

    @media (min-width: 768px) {
      width: 140px; /* larger on desktop */
      height: 140px;
      margin-bottom: 1.25rem;
    }

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .shop-name-container {
    width: 100%;
    margin: 0.25rem 0;
    position: relative;
    z-index: 3;

    input {
      width: 100%;
      text-align: center;
      background: transparent;
      border: none;
      font-size: 1.6rem;
      font-family: ${props => props.theme?.fonts?.heading};
      color: ${props => props.theme?.colors?.accent || '#800000'};
      outline: none;
      padding: 0.5rem;
      
      @media (min-width: 768px) {
        font-size: ${props => props.fontSize || '2.5rem'};
      }

      &:focus {
        color: ${props => props.theme?.colors?.accent || '#800000'};
      }

      &::placeholder {
        color: ${props => `${props.theme?.colors?.accent}80` || 'rgba(128, 0, 0, 0.5)'};
      }
    }
  }

  .shop-description-container {
    width: 100%;
    margin-top: 0.25rem;
    position: relative;
    z-index: 3;

    textarea {
      width: 100%;
      text-align: center;
      background: transparent;
      border: none;
      font-size: 1rem;
      font-family: ${props => props.theme?.fonts?.body};
      color: ${props => props.theme?.colors?.text};
      opacity: 0.8;
      outline: none;
      padding: 0.5rem;
      resize: none;
      min-height: 100px;

      @media (min-width: 768px) {
        font-size: 1.1rem;
      }

      &:focus {
        opacity: 1;
      }

      &::placeholder {
        color: ${props => props.theme?.colors?.text};
        opacity: 0.5;
      }
    }
  }
`;

const TabContentContainer = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
  position: relative;
  min-height: auto;
  height: auto;
`;

  const Header = styled.header`
  width: 100%;
  padding: 0 0.5rem 0 0.75rem;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme?.colors?.headerBg || 'rgba(0, 0, 0, 1)'};
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent}4D` || 'rgba(128, 0, 0, 0.3)'};
  position: fixed;
  top: 0;
  z-index: 100;

  @media (max-width: 767px) {
    ${props => props.$shopTab && `
      background: transparent;
      border-bottom: none;
      backdrop-filter: none;
    `}
  }

  @media (min-width: 768px) {
    padding: 0 2rem;
    min-height: 120px;
  }
`;

// UPDATE HeaderTopRow styled component (around line 1075)
const HeaderTopRow = styled.div`
  width: 100%;
  height: 65px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 768px) {
    height: 120px; /* Increased from 80px to match new header height */
  }
`;

// UPDATE Logo styled component for larger text (around line 200)
const Logo = styled.div`
  color: ${props => props.theme?.colors?.accent || '#800000'};
  font-family: ${props => props.theme?.fonts?.heading || "'Impact', sans-serif"};
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  transform: skew(-5deg);
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;

  @media (min-width: 768px) {
    font-size: 2.2rem; /* Increased from 1.6rem */
    letter-spacing: 2px; /* Increased from 1.5px */
    max-width: 400px;
  }
`;

// UPDATE LogoImage for larger size (around line 185)
const LogoImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.3s ease;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    width: 56px; /* Increased from 40px */
    height: 56px; /* Increased from 40px */
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (hover: hover) {
    &:hover {
      transform: scale(1.05);
    }
  }
`;

// UPDATE HeaderButton for larger icons (around line 900)
const HeaderButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  opacity: 0.8;
  
  &:active {
    transform: scale(0.9);
    opacity: 1;
  }
  
  @media (hover: hover) {
    &:hover {
      opacity: 1;
      background: ${props => `${props.theme?.colors?.accent}10` || 'rgba(128, 0, 0, 0.1)'};
    }
  }
  
  &.pinned {
    color: ${props => props.theme?.colors?.accent || '#800000'};
    opacity: 1;
  }
  
  &.spinning {
    animation: spin 0.5s ease-in-out;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  svg {
    width: 20px;
    height: 20px;
    
    @media (min-width: 768px) {
      width: 28px; /* Increased from 22px */
      height: 28px; /* Increased from 22px */
    }
  }
`;

// UPDATE HeaderTabButton for larger tabs (around line 1095)
const HeaderTabButton = styled.button`
  background: transparent;
  backdrop-filter: blur(10px);
  border: none;
  border-bottom: 3px solid ${props => props.active ?
    props.theme?.colors?.accent :
    'transparent'};
  color: ${props => props.active ? 
    props.theme?.colors?.accent : 
    `${props.theme?.colors?.text}70`};
  padding: 0.5rem 1rem;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  flex-direction: row;
  
  @media (max-width: 767px) {
    padding: 0.4rem 0.75rem;
    gap: 0.4rem;
    border-radius: 6px 6px 0 0;
    
    svg {
      width: 16px !important;
      height: 16px !important;
    }
    
    .tab-label {
      font-size: 0.8rem !important;
      font-weight: ${props => props.active ? '700' : '600'} !important;
      letter-spacing: 0.3px !important;
    }
  }
  
  @media (min-width: 768px) {
    padding: 1rem 2.5rem; /* Increased padding */
    gap: 1rem; /* Increased gap */
    border-bottom-width: 4px; /* Thicker border */
  }
  
  @media (hover: hover) and (min-width: 768px) {
    &:hover {
      color: ${props => props.theme?.colors?.accent};
      background: ${props => `${props.theme?.colors?.accent}08`};
      border-bottom-color: ${props => props.theme?.colors?.accent};
      transform: translateY(-3px);
      
      ${props => !props.active && `
        box-shadow: 
          0 0 10px ${props.theme?.colors?.accent}60,
          0 0 20px ${props.theme?.colors?.accent}40,
          0 0 30px ${props.theme?.colors?.accent}20;
      `}
      
      ${props => props.active && `
        text-shadow: 
          0 0 12px ${props.theme?.colors?.accent},
          0 0 24px ${props.theme?.colors?.accent},
          0 0 36px ${props.theme?.colors?.accent},
          0 0 48px ${props.theme?.colors?.accent};
        box-shadow: 
          0 0 20px ${props.theme?.colors?.accent}95,
          0 0 40px ${props.theme?.colors?.accent}75,
          0 0 60px ${props.theme?.colors?.accent}55,
          0 0 80px ${props.theme?.colors?.accent}35,
          inset 0 0 20px ${props.theme?.colors?.accent}30;
      `}
    }
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  svg {
    width: 15.6px;
    height: 15.6px;
    flex-shrink: 0;
    transition: transform 0.3s ease;
    filter: ${props => props.active ? 
      `drop-shadow(0 0 5px ${props.theme?.colors?.accent}) 
       drop-shadow(0 0 10px ${props.theme?.colors?.accent})` : 
      'none'};
    
    @media (min-width: 768px) {
      width: 24px; /* Increased from 19px */
      height: 24px; /* Increased from 19px */
      filter: ${props => props.active ? 
        `drop-shadow(0 0 8px ${props.theme?.colors?.accent}) 
         drop-shadow(0 0 16px ${props.theme?.colors?.accent})` : 
        'none'};
    }
  }
  
  @media (hover: hover) and (min-width: 768px) {
    &:hover svg {
      transform: translateY(-2px);
      filter: ${props => props.active ? 
        `drop-shadow(0 0 10px ${props.theme?.colors?.accent}) 
         drop-shadow(0 0 20px ${props.theme?.colors?.accent})` : 
        `drop-shadow(0 0 5px ${props.theme?.colors?.accent})`};
    }
  }
  
  .tab-label {
    font-size: 0.85rem;
    font-weight: ${props => props.active ? '700' : '600'};
    text-transform: none;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
    white-space: nowrap;
    
    @media (min-width: 768px) {
      font-size: 1.2rem; /* Increased from 1rem */
      font-weight: ${props => props.active ? '800' : '600'};
      letter-spacing: 1px; /* Increased from 0.8px */
    }
  }
`;

const MainContent = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 15px 0rem 0 0rem;
  position: relative;
  z-index: 1;
  min-height: 100vh;
  
  @media (min-width: 768px) {
    padding: ${props => props.noTopPadding ? '0' : '120px'} 2rem 0 2rem;
    min-height: 120vh;
  }
`;

const ShopTabContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin: 2rem 0 1.5rem;
    padding: 0 1rem;
  `;

const ShopTab = styled.button`
  background: ${props => props.active ?
    props.theme?.colors?.accent || '#800000' : 'transparent'};
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  color: ${props => props.active ?
    'white' : props.theme?.colors?.accent || '#800000'};
  padding: 0.5rem 1rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;

  @media (min-width: 768px) {
    padding: 0.875rem 2.5rem;
    font-size: 1rem;
    gap: 0.5rem;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (hover: hover) {
    &:hover {
      background: ${props => props.active ? 
        props.theme?.colors?.primary || '#4A0404' : 
        `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)'};
    }
  }

  svg {
    width: 14px;
    height: 14px;
  }

  @media (min-width: 768px) {
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

// Main Component
const LiveShopCreation = () => {
  const navigate = useNavigate();
  const { saveTempStore } = useTempStore();
  const { isAuthenticated } = useAuth();
  
  // State management
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [saving, setSaving] = useState(false);
  const [mockFollowers] = useState(() => Math.floor(Math.random() * 9900) + 100);
  const [mockProductsSold] = useState(() => Math.floor(Math.random() * 980) + 20);
  const [mockServicesRendered] = useState(() => Math.floor(Math.random() * 190) + 10);
  const [activeTab, setActiveTab] = useState('home');
  const [selectedTheme, setSelectedTheme] = useState(WELCOME_STYLES.STYLE_1);
  const [shopNameFontSize, setShopNameFontSize] = useState(2.5);
  const [isPinned, setIsPinned] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [validationErrors, setValidationErrors] = useState({});
  const [editingItem, setEditingItem] = useState(null);

  // Add these new state variables at the top of LiveShopCreation component (around line 1000)
  const [shopNameError, setShopNameError] = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [heroBackgroundImage, setHeroBackgroundImage] = useState(null);
  const [shopContentType, setShopContentType] = useState('products');
  const [currentGalleryImage, setCurrentGalleryImage] = useState(0);
  const [isSwipingGallery, setIsSwipingGallery] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
const [selectingLocationFor, setSelectingLocationFor] = useState(null);
const [userLocation, setUserLocation] = useState(null);
const [profileBackgroundImages, setProfileBackgroundImages] = useState([
  'https://images.unsplash.com/photo-1767016311533-8365d673f91b?q=80&w=1318&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', // Earth from space - global reach
  'https://images.unsplash.com/photo-1767111389680-26f5fd91b2a4?q=80&w=1169&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', // Mountain peak success - reaching new heights
  'https://images.unsplash.com/photo-1477346611705-65d1883cee1e?q=80&w=2070&auto=format&fit=crop'  // Sunset horizon - new beginnings
]);
const [currentBgIndex, setCurrentBgIndex] = useState(0);
const [isRestaurant, setIsRestaurant] = useState(false);

const [showBillingDropdown, setShowBillingDropdown] = useState(false);

const [licensesExpanded, setLicensesExpanded] = useState(false);
const [websiteExpanded, setWebsiteExpanded] = useState(false);
const [externalWebsiteUrl, setExternalWebsiteUrl] = useState('');
const [importLoading, setImportLoading] = useState(false);
const [importError, setImportError] = useState('');
const [importedItems, setImportedItems] = useState([]);
const [importedShopMeta, setImportedShopMeta] = useState(null);
const [selectedImportIds, setSelectedImportIds] = useState(new Set());

const [isManuallyControlled, setIsManuallyControlled] = useState(false);

useEffect(() => {
  if (isManuallyControlled) return;
  
  const interval = setInterval(() => {
    setCurrentGalleryImage((prev) => (prev + 1) % profileBackgroundImages.length);
  }, 8000);
  return () => clearInterval(interval);
}, [profileBackgroundImages.length, isManuallyControlled]);

// In swipe handlers:
const handleGallerySwipeLeft = () => {
  setIsManuallyControlled(true);
  setCurrentGalleryImage((prev) => (prev + 1) % placeholderImages.length);
  setTimeout(() => setIsManuallyControlled(false), 10000); // Resume after 10s
};

useEffect(() => {
  const interval = setInterval(() => {
    setCurrentBgIndex((prev) => (prev + 1) % profileBackgroundImages.length);
  }, 9000); // Change image every 5 seconds

  return () => clearInterval(interval);
}, [profileBackgroundImages.length]);

useEffect(() => {
  // Use IP-based location by default (no permission prompt)
  const getIPLocation = async () => {
    // Check if we already have a cached location (avoid repeated API calls)
    const cachedLocation = sessionStorage.getItem('userLocation');
    if (cachedLocation) {
      try {
        const parsed = JSON.parse(cachedLocation);
        console.log('📍 Using cached location:', parsed);
        setUserLocation(parsed);
        return;
      } catch (e) {
        // Invalid cache, continue to fetch
      }
    }

    // Default fallback location (Houston)
    const fallbackLocation = {
      latitude: 29.7604,
      longitude: -95.3698,
      city: 'Houston',
      region: 'Texas',
      country: 'United States',
      source: 'fallback'
    };

    // List of IP geolocation services to try (in order of preference)
    const ipServices = [
      {
        url: 'https://ipwho.is/',
        parse: (data) => ({
          latitude: data.latitude,
          longitude: data.longitude,
          city: data.city,
          region: data.region,
          country: data.country,
          source: 'ipwhois'
        })
      },
      {
        url: 'https://ip-api.com/json/?fields=status,country,regionName,city,lat,lon',
        parse: (data) => data.status === 'success' ? {
          latitude: data.lat,
          longitude: data.lon,
          city: data.city,
          region: data.regionName,
          country: data.country,
          source: 'ip-api'
        } : null
      }
    ];

    // Try each service until one works
    for (const service of ipServices) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

        const response = await fetch(service.url, { 
          signal: controller.signal,
          mode: 'cors'
        });
        clearTimeout(timeoutId);

        if (!response.ok) continue;

        const data = await response.json();
        const location = service.parse(data);

        if (location && location.latitude && location.longitude) {
          console.log(`📍 Got location from ${service.url}:`, location);
          
          // Cache the location for this session
          sessionStorage.setItem('userLocation', JSON.stringify(location));
          setUserLocation(location);
          return;
        }
      } catch (error) {
        // Silently continue to next service
        console.log(`⏭️ Service ${service.url} failed, trying next...`);
        continue;
      }
    }

    // All services failed, use fallback
    console.log('📍 Using fallback location (all IP services failed)');
    sessionStorage.setItem('userLocation', JSON.stringify(fallbackLocation));
    setUserLocation(fallbackLocation);
  };

  // Only run if we don't already have a location
  if (!userLocation) {
    getIPLocation();
  }
}, []);

const profileBgFadeAnimation = `
  @keyframes profileBgFade {
    0%, 100% { opacity: 0; }
    33.33% { opacity: 1; }
    66.66% { opacity: 1; }
  }
`;

// UPDATE the handleItemAdd function (around line 1350)
const handleItemAdd = () => {
  const newItem = {
    id: Date.now().toString(),
    name: 'Item Name',
    price: '',
    description: '',
    category: isRestaurant ? 'Appetizers' : 'Other', // Use restaurant category if toggle is on
    images: [null, null, null],
    currentImageIndex: 0,
    address: '',
    coordinates: null,
    tags: [],
    quantity: 1
  };

  // Add to beginning instead of end
  setShopData(prev => ({
    ...prev,
    items: [newItem, ...prev.items]
  }));
};

// UPDATE the handleAddService function (around line 1380)
const handleAddService = () => {
  const newService = {
    id: Date.now().toString(),
    name: '',
    price: '',
    description: '',
    category: 'Other', // Services always use service categories
    images: [null, null, null],
    currentImageIndex: 0,
    address: '',
    coordinates: null,
    slots: 1
  };

  // Add to beginning
  setShopData(prev => ({
    ...prev,
    services: [newService, ...(prev.services || [])]
  }));
};

// ADD useEffect to update existing items when restaurant toggle changes (add after other useEffects, around line 1150)
useEffect(() => {
  const currentCategories = isRestaurant ? RESTAURANT_CATEGORIES : ITEM_CATEGORIES;
  const needsUpdate = shopData.items?.some(item => 
    !currentCategories.includes(item.category)
  );
  
  if (needsUpdate) {
    setShopData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (!currentCategories.includes(item.category)) {
          return { ...item, category: isRestaurant ? 'Appetizers' : 'Other' };
        }
        return item;
      })
    }));
  }
}, [isRestaurant]); // Run when restaurant toggle changes

// Add handler for requesting location
const handleRequestLocation = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Unable to get your location. Please enable location services.');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }
};


  useEffect(() => {
  requestAnimationFrame(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  });
}, [activeTab]);

  // Auto-rotate gallery images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentGalleryImage((prev) => (prev + 1) % profileBackgroundImages.length);
    }, 8000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [profileBackgroundImages.length]);

  

const handleOpenMapForSelection = (itemId, isService = false) => {
  setSelectingLocationFor({ itemId, isService });
  setMapOpen(true);
};

// In LiveShopCreation.js and ShopPage.js
const handleMapLocationSelect = (coordinates) => {
  console.log('🎯 Map location selected:', coordinates);
  
  if (!selectingLocationFor) {
    console.warn('⚠️ No item selected for location');
    return;
  }
  
  const { itemId, isService } = selectingLocationFor;
  
  // Format coordinates properly
  const formattedCoords = {
    lat: coordinates.lat,
    lng: coordinates.lng,
    latitude: coordinates.lat,
    longitude: coordinates.lng
  };
  
  const formattedAddress = `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`;
  
  console.log('📝 Updating item:', itemId, 'with coords:', formattedCoords);
  
  const updateData = {
    address: formattedAddress,
    coordinates: formattedCoords
  };
  
  // Update the main data
  if (isService) {
    handleServiceUpdate(itemId, updateData);
  } else {
    handleItemUpdate(itemId, updateData);
  }
  
  // ⭐ ADD THIS: If editing item in modal, update editingItem state too
  if (editingItem && editingItem.id === itemId) {
    console.log('✅ Also updating editingItem state');
    setEditingItem(prev => ({
      ...prev,
      ...updateData
    }));
  }
  
  // Close map and clear selection
  setMapOpen(false);
  setSelectingLocationFor(null);
  
  console.log('✅ Location selection complete');
};

  const [shopData, setShopData] = useState({
    name: '', // ADD DEFAULT NAME HERE
    description: '',
    profile: null,
    mission: '',
    selectedHomeTemplate: 1,
    items: [
      {
        id: `${Date.now()}-1`,
        name: '',
        price: '',
        description: '',
        category: 'Other',
        images: [null, null, null],
        currentImageIndex: 0,
        address: '',
        coordinates: null,
        tags: [],
        quantity: 1
      },
      {
        id: `${Date.now()}-2`,
        name: '',
        price: '',
        description: '',
        category: 'Other',
        images: [null, null, null],
        currentImageIndex: 0,
        address: '',
        coordinates: null,
        tags: [],
        quantity: 1
      },
      {
        id: `${Date.now()}-3`,
        name: '',
        price: '',
        description: '',
        category: 'Other',
        images: [null, null, null],
        currentImageIndex: 0,
        address: '',
        coordinates: null,
        tags: [],
        quantity: 1
      }
    ],
    services: [
      {
        id: `${Date.now()}-s1`,
        name: '',
        price: '',
        description: '',
        category: 'Other',
        images: [null, null, null],
        currentImageIndex: 0,
        address: '',
        coordinates: null,
        tags: [],
        isService: true,
        slots: 1
      },
      {
        id: `${Date.now()}-s2`,
        name: '',
        price: '',
        description: '',
        category: 'Other',
        images: [null, null, null],
        currentImageIndex: 0,
        address: '',
        coordinates: null,
        tags: [],
        isService: true,
        slots: 1
      },
      {
        id: `${Date.now()}-s3`,
        name: '',
        price: '',
        description: '',
        category: 'Other',
        images: [null, null, null],
        currentImageIndex: 0,
        address: '',
        coordinates: null,
        tags: [],
        isService: true,
        slots: 1
      }
    ],
    layout: {
      namePosition: 'left',
      tabPosition: 'top'
    }
  });

  // Add this function to check username availability
  // UPDATE checkUsernameAvailability function (around line 1050)
const checkUsernameAvailability = async (shopName) => {
  // Skip check for empty or default names
  if (!shopName || shopName.trim() === '' ) {
    setShopNameError('');
    setUsernameAvailable(null);
    return;
  }

  setCheckingUsername(true);
  setShopNameError('');
  setUsernameAvailable(null);

  try {
    // Generate potential username from shop name
    const potentialUsername = shopName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 20);

    if (!potentialUsername) {
      setShopNameError('Shop name must contain letters or numbers');
      setUsernameAvailable(false);
      setCheckingUsername(false);
      return;
    }

    // Check if username exists
    const { usernameExists } = await import('../../firebase/firebaseService');
    const exists = await usernameExists(potentialUsername);

    if (exists) {
      setShopNameError('This shop name is already taken');
      setUsernameAvailable(false);
    } else {
      setUsernameAvailable(true);
    }
  } catch (error) {
    console.error('Error checking username:', error);
    setShopNameError('Error checking availability');
    setUsernameAvailable(false);
  } finally {
    setCheckingUsername(false);
  }
};

  // Add debounced username check
  // UPDATE the debounced username check useEffect (around line 1100)
useEffect(() => {
  // Don't check on initial mount or if name is the default placeholder
  if (!shopData?.name || shopData.name.trim() === '') {
    setShopNameError('');
    setUsernameAvailable(null);
    return;
  }

  const timer = setTimeout(() => {
    checkUsernameAvailability(shopData.name);
  }, 500); // Check 500ms after user stops typing

  return () => clearTimeout(timer);
}, [shopData?.name]); // Add optional chaining


  

  // Add this useEffect for browser back button handling
  useEffect(() => {
    const handlePopState = (event) => {
      if (editingItem) {
        event.preventDefault();
        setEditingItem(null);
        // Push state again so user doesn't accidentally leave the page
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    // Push initial state when modal opens
    if (editingItem) {
      window.history.pushState(null, '', window.location.pathname);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [editingItem]);

  // Add these handler functions inside your component
  const handleMobileNextImage = (e, itemId) => {
    e.stopPropagation();
    const item = shopData.items.find(i => i.id === itemId);
    if (item && item.images) {
      const validImages = item.images.filter(Boolean);
      if (validImages.length > 1) {
        const currentIndex = item.currentImageIndex || 0;
        const newIndex = (currentIndex + 1) % validImages.length;
        handleItemUpdate(itemId, { currentImageIndex: newIndex });
      }
    }
  };

  const handleMobilePrevImage = (e, itemId) => {
    e.stopPropagation();
    const item = shopData.items.find(i => i.id === itemId);
    if (item && item.images) {
      const validImages = item.images.filter(Boolean);
      if (validImages.length > 1) {
        const currentIndex = item.currentImageIndex || 0;
        const newIndex = (currentIndex - 1 + validImages.length) % validImages.length;
        handleItemUpdate(itemId, { currentImageIndex: newIndex });
      }
    }
  };

  useEffect(() => {
  const handleClickOutside = (event) => {
    if (showBillingDropdown && !event.target.closest('[class*="BillingDropdown"]')) {
      setShowBillingDropdown(false);
    }
  };

  if (showBillingDropdown) {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }
}, [showBillingDropdown]);

  // Theme management
  useEffect(() => {
    const pinnedStyleId = localStorage.getItem('pinnedStyleId');
    
    if (pinnedStyleId) {
      const pinnedStyle = Object.values(WELCOME_STYLES).find(
        style => style.id.toString() === pinnedStyleId
      );

      if (pinnedStyle) {
        setSelectedTheme(pinnedStyle);
        setIsPinned(true);
        return;
      }
    }

    const styles = Object.values(WELCOME_STYLES);
    const randomStyle = styles[Math.floor(Math.random() * styles.length)];
    setSelectedTheme(randomStyle);
  }, []);

  const refreshTheme = () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    
    const styles = Object.values(WELCOME_STYLES);
    const otherStyles = styles.filter(style => style.id !== selectedTheme.id);
    
    if (otherStyles.length > 0) {
      const randomStyle = otherStyles[Math.floor(Math.random() * otherStyles.length)];
      setSelectedTheme(randomStyle);

      if (isPinned) {
        localStorage.removeItem('pinnedStyleId');
        setIsPinned(false);
      }
    }

    setTimeout(() => setIsRefreshing(false), 500);
  };

  const togglePinStyle = () => {
    if (isPinned) {
      localStorage.removeItem('pinnedStyleId');
      setIsPinned(false);

      const styles = Object.values(WELCOME_STYLES).filter(
        style => style.id !== selectedTheme.id
      );

      if (styles.length > 0) {
        const randomStyle = styles[Math.floor(Math.random() * styles.length)];
        setSelectedTheme(randomStyle);
      }
    } else {
      localStorage.setItem('pinnedStyleId', selectedTheme.id.toString());
      setIsPinned(true);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Item management
  const handleShopDataChange = (field, value) => {
    setShopData(prev => ({
      ...prev,
      [field]: value
    }));
  };

// UPDATE the toggleItemExpansion function to only affect the clicked item
const toggleItemExpansion = (itemId) => {
  setExpandedItems(prev => {
    const newSet = new Set(prev);
    if (newSet.has(itemId)) {
      newSet.delete(itemId);
    } else {
      newSet.add(itemId);
    }
    return newSet;
  });
};

  const handleItemUpdate = (itemId, updates) => {
    setShopData(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, ...updates } : item
      )
    }));
  };

  const handleServiceUpdate = (serviceId, updates) => {
    setShopData(prev => ({
      ...prev,
      services: prev.services.map(service => 
        service.id === serviceId ? { ...service, ...updates } : service
      )
    }));
  };

  const handleItemDelete = (itemId) => {
    setShopData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const handleServiceDelete = (serviceId) => {
    setShopData(prev => ({
      ...prev,
      services: prev.services.filter(service => service.id !== serviceId)
    }));
  };


const handleGallerySwipeRight = () => {
  setCurrentGalleryImage((prev) => (prev - 1 + placeholderImages.length) % placeholderImages.length);
};

const galleryTouchStart = useRef(null);
const galleryTouchEnd = useRef(null);

const handleGalleryTouchStart = (e) => {
  setIsSwipingGallery(true);
  galleryTouchStart.current = e.targetTouches[0].clientX;
};

const handleGalleryTouchMove = (e) => {
  galleryTouchEnd.current = e.targetTouches[0].clientX;
};

const handleGalleryTouchEnd = () => {
  if (!galleryTouchStart.current || !galleryTouchEnd.current) {
    setIsSwipingGallery(false);
    return;
  }
  
  const distance = galleryTouchStart.current - galleryTouchEnd.current;
  const minSwipeDistance = 50;
  
  if (distance > minSwipeDistance) {
    handleGallerySwipeLeft(); // Swipe left = next image
  } else if (distance < -minSwipeDistance) {
    handleGallerySwipeRight(); // Swipe right = previous image
  }
  
  galleryTouchStart.current = null;
  galleryTouchEnd.current = null;
  
  // Delay resetting the flag to prevent tab switch
  setTimeout(() => {
    setIsSwipingGallery(false);
  }, 100);
};

// UPDATE handleSave function in LiveShopCreation.js (around line 1200)
const handleSave = async () => {
  // Check if shop name is still default or empty
  let finalShopName = shopData.name;
  
  if (!finalShopName || finalShopName === '') {
    const adjectives = ['Cool', 'Great', 'Super', 'Amazing', 'Awesome', 'Epic', 'Prime', 'Elite'];
    const nouns = ['Shop', 'Store', 'Market', 'Bazaar', 'Outlet', 'Hub', 'Spot', 'Place'];
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    const randomNumber = Math.floor(Math.random() * 1000);
    
    finalShopName = `${randomAdjective}${randomNoun}${randomNumber}`;
  }

  if (shopNameError || usernameAvailable === false) {
    alert('Please choose a different shop name - this one is already taken');
    return;
  }

  const shopValidation = validateShopData({ ...shopData, name: finalShopName });
  const itemsValidation = validateAllItems(shopData.items);

  if (!shopValidation.isValid || !itemsValidation.isValid) {
    setValidationErrors({
      shop: shopValidation.errors,
      items: itemsValidation.itemErrors
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
    alert('Please fix validation errors before saving');
    return;
  }

  

  // Template home sections that will be saved
  const homeSections = [
    {
      id: 'hero-1',
      type: 'hero-banner',
      order: 0,
      config: {
        headline: finalShopName,
        subtitle: 'Welcome to ' + finalShopName,
        backgroundImage: null,
        height: '70vh'
      }
    },
    {
      id: 'mission-1',
      type: 'mission-statement',
      order: 1,
      config: {
        title: 'Mission',
        content: 'We have top quality products ready to be delivered with exceptional customer service. Our commitment to quality  drives everything we do.'
      }
    },
    {
      id: 'featured-1',
      type: 'featured-items',
      order: 2,
      config: {
        title: 'Featured Products',
        itemCount: 4
      }
    },
    {
      id: 'gallery-1',
      type: 'photo-gallery',
      order: 3,
      config: {
        title: 'Gallery',
        images: [
          'https://images.unsplash.com/photo-1759866221951-38c624319c98?q=80&w=1228&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
          'https://images.unsplash.com/photo-1767016311533-8365d673f91b?q=80&w=1318&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
          'https://images.unsplash.com/photo-1767103431751-58ddefddc370?q=80&w=1169&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
        ]
      }
    }
  ];

  const dataToSave = {
    ...shopData,
    name: finalShopName,
    theme: selectedTheme,
    isRestaurant: isRestaurant,
    selectedHomeTemplate: 1,
    homeSections: homeSections, // This will be used in the actual shop
    profileBackgroundImages: profileBackgroundImages,
    layout: {
      namePosition: shopData.layout.namePosition,
      tabPosition: 'top',
      nameSize: shopNameFontSize
    },
    items: shopData.items || [],
    services: shopData.services || [],
    permits: shopData.permits || [],
    externalWebsite: externalWebsiteUrl.trim() ? { url: externalWebsiteUrl.trim(), enabled: true } : null,
    createdAt: new Date().toISOString()
  };

  // Handle profile image - ensure it's in the correct format
  if (shopData.profile) {
    if (typeof shopData.profile === 'string') {
      dataToSave.profile = shopData.profile;
    } else if (shopData.profile.file) {
      dataToSave.profile = {
        file: shopData.profile.file,
        preview: shopData.profile.preview,
        type: shopData.profile.type,
        name: shopData.profile.name
      };
    }
  }

  console.log('Saving data with profile:', dataToSave.profile);
  console.log('Saving data with home widgets:', dataToSave.homeWidgets);

  // Generate username
  if (dataToSave.name) {
    try {
      dataToSave.username = await generateUsername(dataToSave.name);
    } catch (error) {
      console.error('Error generating username:', error);
      dataToSave.username = dataToSave.name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .substring(0, 20) || 'shop';
    }
  }

  saveTempStore(dataToSave);

  setSaving(true);
  try {
    const result = await safeSignInWithPopup(googleProvider);
    const user = result.user;

    const hasShop = await checkExistingShop(user.uid);
    if (!hasShop) {
      await saveShopData(user.uid, { ...dataToSave, selectedPlan });
    }
    navigate(`/shop/${user.uid}`, { replace: true });
  } catch (err) {
    if (err?.code !== 'auth/popup-closed-by-user' && err?.code !== 'auth/cancelled-popup-request') {
      console.error('Sign-in error:', err);
    }
  } finally {
    setSaving(false);
  }
};

const renderBillingDropdown = () => {
  const PLAN_EMOJI = { free: '🆓', starter: '💙', pro: '💜', elite: '🟡', premium: '🚀' };
  return (
    <BillingExplainer theme={selectedTheme}>
      <BillingTitle theme={selectedTheme}>
        🚀 One-Time Setup Fee
      </BillingTitle>
      <GrowthMessage theme={selectedTheme}>
        {PLAN_ORDER.map((key, i) => {
          const perms = PLAN_PERMISSIONS[key];
          const priceLabel = PLANS[key].price === 0 ? 'Free' : `${PLANS[key].label} $${PLANS[key].price}`;
          const detail = `${perms.maxVisible} visible card${perms.maxVisible !== 1 ? 's' : ''}, ${perms.maxTabs} page${perms.maxTabs !== 1 ? 's' : ''}${perms.canPickupGigs ? ', gig pickup' : ''}${perms.boostedListings ? ', boosted listings' : ''}`;
          return (
            <React.Fragment key={key}>
              <strong>{PLAN_EMOJI[key]} {priceLabel}</strong> — {detail}
              {i < PLAN_ORDER.length - 1 && <br/>}
            </React.Fragment>
          );
        })}
      </GrowthMessage>
    </BillingExplainer>
  );
};

  const renderShopView = () => (
  <MainContent>
    <style>{profileBgFadeAnimation}</style>
    
    <div style={{
        margin: '0 0rem',
        marginTop: '-15px', // Pull section flush to viewport top so cover image fills behind transparent header
        marginBottom: '2rem'
      }}>
    {/* Profile Section with Slideshow Background */}
<ShopProfileSection
  fontSize={shopNameFontSize}
  style={{
    position: 'relative',
    overflow: 'hidden',
    maxWidth: '100%',
    margin: '0 0 -2rem 0',
    padding: window.innerWidth <= 768 ? '8rem 1rem 3rem' : '4rem 1rem 3rem',
    borderRadius: '0',
  }}
>
  {/* Slideshow Background */}
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    overflow: 'hidden'
  }}>
    {profileBackgroundImages.map((image, index) => (
      <div
        key={index}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: currentBgIndex === index ? 2 : 1,
          transition: 'opacity 0.8s ease-in-out',
          transform: 'scale(1.1)'
        }}
      />
    ))}
  </div>
  
  {/* Profile Content - Keep original structure with relative positioning only */}
  <div className="profile-image" style={{ position: 'relative', zIndex: 1 }}>
    <EditableImage
      value={shopData.profile}
      onChange={(value) => handleShopDataChange('profile', value)}
      theme={selectedTheme}
      paddingTop="2rem"
      round
      width="100%"
      height="100%"
    />
  </div>
      <div className="shop-name-container" style={{ position: 'relative', paddingTop: '.05rem', zIndex: 5 }}>
        <ShopNameInputContainer>
          <ShopNameInput
            value={shopData?.name || ''}
            onChange={(e) => {
              handleShopDataChange('name', e.target.value);
            }}
            placeholder="*Store Name*"
            fontSize={shopNameFontSize}
            theme={selectedTheme}
            isError={!!shopNameError}
          />
          <FadingUnderline theme={selectedTheme} />
          {checkingUsername && (
            <ShopNameError theme={selectedTheme}>
              Checking availability...
            </ShopNameError>
          )}
          {shopNameError && (
            <ShopNameError theme={selectedTheme}>
              {shopNameError}
            </ShopNameError>
          )}
          {usernameAvailable && !checkingUsername && shopData.name !== '' && (
            <ShopNameSuccess theme={selectedTheme}>
              ✓ Shop name available
            </ShopNameSuccess>
          )}
        </ShopNameInputContainer>
      </div>
      <div className="shop-subdomain-container" style={{ 
  position: 'relative', 
  zIndex: 3,
  marginTop: '1rem',
  padding: '0.75rem 1rem',
  background: `${selectedTheme?.colors?.surface}30`,
  borderRadius: '8px',
  border: `1px solid ${selectedTheme?.colors?.accent}30`
}}>
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap'
  }}>
    <span style={{
      fontSize: '1rem',
      fontWeight: '600',
      color: selectedTheme?.colors?.accent,
      fontFamily: selectedTheme?.fonts?.body,
      wordBreak: 'break-all',
      textAlign: 'center'
    }}>
      {shopData?.name ? 
        `${shopData.name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}.KalKode.Com` : 
        'StoreName.KalKode.Com'
      }
    </span>
  </div>
</div>
    </ShopProfileSection>
    </div>


    {/* Conditional Rendering based on shopContentType */}
    {shopContentType === 'products' ? (
  <div style={{
    background: selectedTheme?.colors?.background,
    padding: 'clamp(1rem, 3vw, 2rem)',
    borderRadius: 'clamp(12px, 2vw, 16px)',
    border: `2px solid ${selectedTheme?.colors?.accent}30`,
    marginTop: '0rem'
  }}>

    {/* Shop Content Type Tabs with Backdrop */}
    <ShopTabContainer>
      <ShopTab
        active={shopContentType === 'products'}
        onClick={() => setShopContentType('products')}
        theme={selectedTheme}
      >
        <Package size={18} />
        Products
      </ShopTab>
      <ShopTab
        active={shopContentType === 'services'}
        onClick={() => setShopContentType('services')}
        theme={selectedTheme}
      >
        <Store size={18} />
        Services
      </ShopTab>
    </ShopTabContainer>

    {/* Section Divider */}
    <FadingUnderline theme={selectedTheme} />

    {/* External Website Import Section */}
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{
        background: `${selectedTheme?.colors?.surface}30`,
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        border: `1px solid ${selectedTheme?.colors?.accent}30`,
        overflow: 'hidden'
      }}>
        {/* Collapsible Header */}
        <div
          onClick={() => setWebsiteExpanded(!websiteExpanded)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: window.innerWidth <= 768 ? '0.75rem 1rem' : '1.25rem 1.5rem', cursor: 'pointer', userSelect: 'none' }}
          onMouseEnter={e => { if (window.innerWidth > 768) e.currentTarget.style.background = `${selectedTheme?.colors?.accent}10`; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h3 style={{ margin: 0, color: selectedTheme?.colors?.accent, fontSize: window.innerWidth <= 768 ? '0.82rem' : '1rem', fontWeight: '600' }}>
              🌐 Import from a Website
            </h3>
            {shopData.items?.some(i => i.source === 'external') && (
              <span style={{ background: selectedTheme?.colors?.accent, color: 'white', padding: '0.2rem 0.5rem', borderRadius: '12px', fontSize: '0.72rem', fontWeight: '700' }}>
                {shopData.items.filter(i => i.source === 'external').length} imported
              </span>
            )}
          </div>
          <div style={{ color: selectedTheme?.colors?.accent, transform: websiteExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', display: 'flex', alignItems: 'center' }}>
            <ChevronDown size={20} />
          </div>
        </div>

        {/* Collapsible Content */}
        <div style={{ maxHeight: websiteExpanded ? '2000px' : '0', opacity: websiteExpanded ? 1 : 0, overflow: 'hidden', transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)' }}>
          <div style={{ padding: websiteExpanded ? '0 1.5rem 1.5rem' : '0' }}>
            <p style={{ fontSize: '0.85rem', color: selectedTheme?.colors?.text, opacity: 0.7, marginBottom: '1.25rem', marginTop: 0 }}>
              Import from your existing website.
            </p>

            {/* URL Input Row */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexDirection: window.innerWidth <= 768 ? 'column' : 'row' }}>
              <input
                type="url"
                placeholder="https://yourstore.com"
                value={externalWebsiteUrl}
                onChange={e => { setExternalWebsiteUrl(e.target.value); setImportError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleWebsiteImport()}
                style={{
                  flex: '1', width: window.innerWidth <= 768 ? '100%' : 'auto',
                  padding: window.innerWidth <= 768 ? '0.5rem 0.75rem' : '0.65rem 1rem',
                  borderRadius: '10px', border: `1px solid ${selectedTheme?.colors?.accent}40`,
                  background: `${selectedTheme?.colors?.background}60`, color: selectedTheme?.colors?.text,
                  fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem', outline: 'none', fontFamily: selectedTheme?.fonts?.body,
                  boxSizing: 'border-box'
                }}
              />
              <button
                onClick={handleWebsiteImport}
                disabled={importLoading || !externalWebsiteUrl.trim()}
                style={{
                  padding: window.innerWidth <= 768 ? '0.5rem 1rem' : '0.65rem 1.4rem',
                  borderRadius: '10px', border: 'none', cursor: 'pointer',
                  background: selectedTheme?.colors?.accent, color: 'white', fontWeight: '600',
                  fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem',
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  opacity: (importLoading || !externalWebsiteUrl.trim()) ? 0.6 : 1,
                  transition: 'opacity 0.2s ease', whiteSpace: 'nowrap'
                }}
              >
                {importLoading ? 'Scanning...' : 'Import Products'}
              </button>
            </div>

            {/* Error */}
            {importError && (
              <p style={{ color: '#f87171', fontSize: '0.85rem', marginTop: '0.75rem', marginBottom: 0 }}>{importError}</p>
            )}

            {/* Results */}
            {importedItems.length > 0 && (
              <div style={{ marginTop: '1.25rem' }}>
                {importedShopMeta?.name && (
                  <p style={{ fontSize: '0.85rem', color: selectedTheme?.colors?.accent, marginBottom: '0.75rem', fontWeight: '600' }}>
                    Found {importedItems.length} product{importedItems.length !== 1 ? 's' : ''} from <em>{importedShopMeta.name}</em>
                  </p>
                )}

                {/* Select/deselect all */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                  <span style={{ fontSize: '0.8rem', color: selectedTheme?.colors?.text, opacity: 0.7 }}>
                    {selectedImportIds.size} of {importedItems.length} selected
                  </span>
                  <button
                    onClick={() => setSelectedImportIds(
                      selectedImportIds.size === importedItems.length
                        ? new Set()
                        : new Set(importedItems.map((_, i) => i))
                    )}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: selectedTheme?.colors?.accent, fontSize: '0.8rem', fontWeight: '600', padding: 0 }}
                  >
                    {selectedImportIds.size === importedItems.length ? 'Deselect all' : 'Select all'}
                  </button>
                </div>

                {/* Item list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '320px', overflowY: 'auto' }}>
                  {importedItems.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedImportIds(prev => {
                        const next = new Set(prev);
                        next.has(idx) ? next.delete(idx) : next.add(idx);
                        return next;
                      })}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '0.6rem 0.8rem', borderRadius: '10px', cursor: 'pointer',
                        background: selectedImportIds.has(idx) ? `${selectedTheme?.colors?.accent}18` : `${selectedTheme?.colors?.surface}20`,
                        border: `1px solid ${selectedImportIds.has(idx) ? selectedTheme?.colors?.accent + '50' : 'transparent'}`,
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <div style={{
                        width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                        border: `2px solid ${selectedImportIds.has(idx) ? selectedTheme?.colors?.accent : selectedTheme?.colors?.text + '50'}`,
                        background: selectedImportIds.has(idx) ? selectedTheme?.colors?.accent : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        {selectedImportIds.has(idx) && <Check size={11} color="white" strokeWidth={3} />}
                      </div>
                      {item.image && (
                        <img src={item.image} alt={item.name} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} onError={e => e.target.style.display = 'none'} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '600', fontSize: '0.85rem', color: selectedTheme?.colors?.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                        {item.description && <div style={{ fontSize: '0.75rem', color: selectedTheme?.colors?.text, opacity: 0.6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.description}</div>}
                      </div>
                      <div style={{ fontWeight: '700', fontSize: '0.9rem', color: selectedTheme?.colors?.accent, flexShrink: 0 }}>
                        {item.price ? `$${parseFloat(item.price).toFixed(2)}` : '—'}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add button */}
                <button
                  onClick={handleAddImportedItems}
                  disabled={selectedImportIds.size === 0}
                  style={{
                    marginTop: '1rem', width: '100%', padding: '0.75rem',
                    borderRadius: '10px', border: 'none', cursor: selectedImportIds.size === 0 ? 'not-allowed' : 'pointer',
                    background: selectedTheme?.colors?.accent, color: 'white', fontWeight: '700',
                    fontSize: '0.95rem', opacity: selectedImportIds.size === 0 ? 0.5 : 1,
                    transition: 'opacity 0.2s ease'
                  }}
                >
                  Add {selectedImportIds.size} Product{selectedImportIds.size !== 1 ? 's' : ''} to Shop
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    <ItemsContainer>
      <ItemsGrid>
        {/* ADD: Placeholder card as first item */}
        <React.Fragment key="add-product-placeholder">
          {/* Mobile Placeholder Card */}
          {window.innerWidth < 767 && (
          
          <MobileTemplateCard
            theme={selectedTheme}
            onClick={handleItemAdd}
            style={{
              cursor: 'pointer',
              border: `2px dashed ${selectedTheme?.colors?.accent}40`,
              background: `${selectedTheme?.colors?.surface}20`
            }}
          >
            <MobileTemplateImageContainer theme={selectedTheme}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                color: selectedTheme?.colors?.accent,
                opacity: 0.6,
                height: '100%',
                width: '100%'
              }}>
                <Plus size={32} />
                <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                  Add Product
                </span>
              </div>
            </MobileTemplateImageContainer>
            
            <MobileTemplateContent theme={selectedTheme}>
              <div className="item-name" style={{ opacity: 0.5, textAlign: 'center' }}>
                Tap to add
              </div>
            </MobileTemplateContent>
          </MobileTemplateCard>
          )}


          {/* Desktop Placeholder Card */}
          {window.innerWidth > 767 && (

          <DesktopItemCard
            theme={selectedTheme}
            onClick={handleItemAdd}
            style={{
              cursor: 'pointer',
              border: `2px dashed ${selectedTheme?.colors?.accent}40`,
              background: `${selectedTheme?.colors?.surface}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px'
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem',
              color: selectedTheme?.colors?.accent,
              opacity: 0.7,
              padding: '2rem'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: `${selectedTheme?.colors?.accent}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}>
                <Plus size={40} />
              </div>
              <span style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                textAlign: 'center'
              }}>
                Add Product
              </span>
            </div>
          </DesktopItemCard>
          )}
        </React.Fragment>

        {shopData.items.map(item => {
              const isExpanded = expandedItems.has(item.id);
              const currentItem = shopData.items.find(i => i.id === item.id);
              const validImages = currentItem.images.filter(Boolean);
              const currentImageIndex = currentItem.currentImageIndex || 0;
              const currentImage = validImages[currentImageIndex] || null;

              return (
                <React.Fragment key={item.id}>
                  {/* Mobile Template Card */}
                  <MobileTemplateCard 
                    theme={selectedTheme}
                    onClick={() => {
                      const itemToEdit = shopData.items.find(i => i.id === item.id);
                      setEditingItem({
                        ...itemToEdit,
                        images: [...itemToEdit.images],
                        currentImageIndex: itemToEdit.currentImageIndex || 0,
                        isService: false
                      });
                    }}
                  >
                    <MobileTemplateImageContainer theme={selectedTheme}>
                      {currentImage ? (
                        <img src={currentImage} alt={currentItem.name || 'Item'} />
                      ) : (
                        <div className="placeholder">
                          <div style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            background: `${selectedTheme?.colors?.accent}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '0.5rem'
                          }}>
                            <Package size={24} color={selectedTheme?.colors?.accent} />
                          </div>
                          <span style={{
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            color: selectedTheme?.colors?.accent,
                            textAlign: 'center'
                          }}>
                            Add Photo
                          </span>
                        </div>
                      )}
                      
                      {validImages.length > 1 && (
                        <>
                          <button 
                            className="mobile-carousel-arrow left"
                            onClick={(e) => handleMobilePrevImage(e, currentItem.id)}
                          >
                            <ChevronLeft size={12} />
                          </button>
                          <button 
                            className="mobile-carousel-arrow right"
                            onClick={(e) => handleMobileNextImage(e, currentItem.id)}
                          >
                            <ChevronRight size={12} />
                          </button>
                          <div className="image-dots">
                            {validImages.map((_, index) => (
                              <div 
                                key={index}
                                className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                      
                      <MobileDeleteButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleItemDelete(currentItem.id);
                        }}
                      >
                        <X size={12} />
                      </MobileDeleteButton>
                    </MobileTemplateImageContainer>
                    
                    <MobileTemplateContent theme={selectedTheme}>
                      <div className="item-name">
                        {currentItem.name && currentItem.name !== 'Item Name' ? 
                          currentItem.name : 
                          <span style={{ opacity: 0.5 }}></span>
                        }
                      </div>
                      <div className="item-price">
                        {currentItem.price ? `$${parseFloat(currentItem.price).toFixed(2)}` : 
                          <span className="empty-text">$0.00</span>}
                      </div>
                    </MobileTemplateContent>
                  </MobileTemplateCard>

                  {/* Desktop Card */}
                  <DesktopItemCard theme={selectedTheme}>
                    <DeleteButton onClick={() => handleItemDelete(item.id)}>
                      <X size={16} />
                    </DeleteButton>
                    
                    <ItemImageContainer theme={selectedTheme}>
                      <div className="image-container">
                        {item.images[item.currentImageIndex] ? (
                          <EditableImage
                            value={item.images[item.currentImageIndex]}
                            onChange={(value) => {
                              const newImages = [...item.images];
                              newImages[item.currentImageIndex] = value;
                              handleItemUpdate(item.id, { images: newImages });
                            }}
                            theme={selectedTheme}
                            height="100%"
                            width="100%"
                          />
                        ) : (
                          <div 
                            className="placeholder"
                            onClick={() => document.getElementById(`image-upload-${item.id}-${item.currentImageIndex}`).click()}
                          >
                            <div style={{
                              width: '80px',
                              height: '80px',
                              borderRadius: '50%',
                              background: `${selectedTheme?.colors?.accent}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginBottom: '0.75rem',
                              transition: 'all 0.3s ease'
                            }}>
                              <Package size={36} color={selectedTheme?.colors?.accent} />
                            </div>
                            <span style={{
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              color: selectedTheme?.colors?.accent,
                              marginBottom: '0.25rem'
                            }}>
                              Add Product Photo
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <input
                        type="file"
                        id={`image-upload-${item.id}-${item.currentImageIndex}`}
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            validateImageFile(file, 20);
                          } catch (err) {
                            alert(err.message || 'Invalid image file.');
                            return;
                          }
                          let compressedFile;
                          try {
                            compressedFile = await compressProductImage(file);
                          } catch (err) {
                            console.warn('⚠️ Compression failed, using original file uncompressed:', err);
                            compressedFile = file;
                          }
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const newImages = [...item.images];
                            newImages[item.currentImageIndex] = {
                              file: compressedFile,
                              preview: reader.result,
                              type: compressedFile.type,
                              name: compressedFile.name
                            };
                            handleItemUpdate(item.id, { images: newImages });
                          };
                          reader.readAsDataURL(compressedFile);
                        }}
                      />
                      
                      {validImages.length > 0 && (
                        <>
                          <button 
                            className="carousel-arrow left"
                            onClick={() => {
                              const newIndex = ((item.currentImageIndex - 1) + 3) % 3;
                              handleItemUpdate(item.id, { currentImageIndex: newIndex });
                            }}
                          >
                            <ChevronLeft size={16} />
                          </button>
                          <button 
                            className="carousel-arrow right"
                            onClick={() => {
                              const newIndex = (item.currentImageIndex + 1) % 3;
                              handleItemUpdate(item.id, { currentImageIndex: newIndex });
                            }}
                          >
                            <ChevronRight size={16} />
                          </button>
                        </>
                      )}
                    </ItemImageContainer>

                    <ItemContent>
                    <ItemHeader onClick={() => toggleItemExpansion(item.id)}>
                      <h4>
                        {item.name && item.name !== 'Item Name' ? 
                          item.name : 
                          <span style={{ opacity: 0.5 }}>Item Name</span>
                        }
                      </h4>
                      <ExpandButton>
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </ExpandButton>
                    </ItemHeader>
                      
                    {/* UPDATED: Smooth height transition */}
                    <ItemDetails expanded={isExpanded}>
                      <div className="details-content">
                        <ValidatedEditableText
                          value={item.name}
                          onChange={(value) => handleItemUpdate(item.id, { name: value })}
                          placeholder="Item Name"
                          validationRules={VALIDATION_RULES.item.name}
                          theme={selectedTheme}
                        />
                        <ValidatedEditableText
                          value={item.price}
                          onChange={(value) => handleItemUpdate(item.id, { price: value })}
                          placeholder="Price"
                          validationRules={VALIDATION_RULES.item.price}
                          theme={selectedTheme}
                        />
                        <ValidatedEditableText
                          value={item.description}
                          onChange={(value) => handleItemUpdate(item.id, { description: value })}
                          placeholder="Item Description"
                          validationRules={VALIDATION_RULES.item.description}
                          multiline
                          theme={selectedTheme}
                        />
                        <CategorySelect
  value={item.category || (isRestaurant ? 'Appetizers' : 'Other')}
  onChange={(e) => handleItemUpdate(item.id, { category: e.target.value })}
  theme={selectedTheme}
>
  {(isRestaurant ? RESTAURANT_CATEGORIES : ITEM_CATEGORIES).map(category => (
    <option key={category} value={category}>
      {category}
    </option>
  ))}
</CategorySelect>
                        <QuantitySelector 
                          value={parseInt(item.quantity) || 1}
                          onChange={(value) => handleItemUpdate(item.id, { quantity: value })}
                          theme={selectedTheme}
                          min={0}
                          max={9999}
                        />
                        <AddressInput
  address={item.address || ''}
  onAddressChange={(value) => handleItemUpdate(item.id, { 
    address: value
  })}
  onLocationSelect={(location) => {
    if (location?.coordinates) {
      handleItemUpdate(item.id, {
        address: location.address,
        coordinates: location.coordinates
      });
    } else if (!location?.address) {
      handleItemUpdate(item.id, {
        address: '',
        coordinates: null
      });
    }
  }}
  onMapOpen={() => handleOpenMapForSelection(item.id, false)}
/>
                      </div>
                    </ItemDetails>
                  </ItemContent>
                </DesktopItemCard>
              </React.Fragment>
            );
          })}
          </ItemsGrid>
        </ItemsContainer>
      </div>
  ) : (
  <div style={{
    background: selectedTheme?.colors?.background,
    padding: 'clamp(1rem, 3vw, 2rem)',
    borderRadius: 'clamp(12px, 2vw, 16px)',
    border: `2px solid ${selectedTheme?.colors?.accent}30`,
    marginTop: '1rem'
  }}>

    {/* Shop Content Type Tabs with Backdrop */}
    <ShopTabContainer>
      <ShopTab
        active={shopContentType === 'products'}
        onClick={() => setShopContentType('products')}
        theme={selectedTheme}
      >
        <Package size={18} />
        Products
      </ShopTab>
      <ShopTab
        active={shopContentType === 'services'}
        onClick={() => setShopContentType('services')}
        theme={selectedTheme}
      >
        <Store size={18} />
        Services
      </ShopTab>
    </ShopTabContainer>
    <ItemsContainer>
      <ItemsGrid>
        {/* ADD: Placeholder card as first item */}
        <React.Fragment key="add-service-placeholder">
          {/* Mobile Placeholder Card */}
          {window.innerWidth < 767 && (
          <MobileTemplateCard
            theme={selectedTheme}
            onClick={handleAddService}
            style={{
              cursor: 'pointer',
              border: `2px dashed ${selectedTheme?.colors?.accent}40`,
              background: `${selectedTheme?.colors?.surface}20`
            }}
          >
            <MobileTemplateImageContainer theme={selectedTheme}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                color: selectedTheme?.colors?.accent,
                opacity: 0.6,
                height: '100%',
                width: '100%'
              }}>
                <Plus size={32} />
                <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                  Add Service
                </span>
              </div>
            </MobileTemplateImageContainer>
            
            <MobileTemplateContent theme={selectedTheme}>
              <div className="item-name" style={{ opacity: 0.5, textAlign: 'center' }}>
                Tap to add
              </div>
            </MobileTemplateContent>
          </MobileTemplateCard>
          )}

          {/* Desktop Placeholder Card */}
          {window.innerWidth > 767 && (
          <DesktopItemCard
            theme={selectedTheme}
            onClick={handleAddService}
            style={{
              cursor: 'pointer',
              border: `2px dashed ${selectedTheme?.colors?.accent}40`,
              background: `${selectedTheme?.colors?.surface}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px'
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem',
              color: selectedTheme?.colors?.accent,
              opacity: 0.7,
              padding: '2rem'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: `${selectedTheme?.colors?.accent}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}>
                <Plus size={40} />
              </div>
              <span style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                textAlign: 'center'
              }}>
                Add Service
              </span>
            </div>
          </DesktopItemCard>
      )}

        </React.Fragment>

        {shopData.services.map(service => {
              const isExpanded = expandedItems.has(service.id);
              const currentService = shopData.services.find(s => s.id === service.id);
              const validImages = currentService.images.filter(Boolean);
              const currentImageIndex = currentService.currentImageIndex || 0;
              const currentImage = validImages[currentImageIndex] || null;

              return (
                <React.Fragment key={service.id}>
                  {/* Mobile Service Card */}
                  <MobileTemplateCard 
                    theme={selectedTheme}
                    onClick={() => {
                      const serviceToEdit = shopData.services.find(s => s.id === service.id);
                      setEditingItem({
                        ...serviceToEdit,
                        images: [...serviceToEdit.images],
                        currentImageIndex: serviceToEdit.currentImageIndex || 0,
                        isService: true
                      });
                    }}
                  >
                    <MobileTemplateImageContainer theme={selectedTheme}>
                      {currentImage ? (
                        <img src={currentImage} alt={currentService.name || 'Service'} />
                      ) : (
                        <div className="placeholder">
                          <div style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            background: `${selectedTheme?.colors?.accent}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '0.5rem'
                          }}>
                            <Store size={24} color={selectedTheme?.colors?.accent} />
                          </div>
                          <span>Add Photo</span>
                        </div>
                      )}

                      {validImages.length > 1 && (
                        <>
                          <button 
                            className="mobile-carousel-arrow left"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newIndex = ((currentImageIndex - 1) + validImages.length) % validImages.length;
                              handleServiceUpdate(currentService.id, { currentImageIndex: newIndex });
                            }}
                          >
                            <ChevronLeft size={12} />
                          </button>
                          <button 
                            className="mobile-carousel-arrow right"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newIndex = (currentImageIndex + 1) % validImages.length;
                              handleServiceUpdate(currentService.id, { currentImageIndex: newIndex });
                            }}
                          >
                            <ChevronRight size={12} />
                          </button>
                          <div className="image-dots">
                            {validImages.map((_, index) => (
                              <div 
                                key={index}
                                className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                              />
                            ))}
                          </div>
                        </>
                      )}

                      <MobileDeleteButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServiceDelete(currentService.id);
                        }}
                      >
                        <X size={12} />
                      </MobileDeleteButton>
                    </MobileTemplateImageContainer>
                    
                    <MobileTemplateContent theme={selectedTheme}>
                      <div className="item-name">
                        {currentService.name && currentService.name !== 'Service Name' ? 
                          currentService.name : 
                          <span style={{ opacity: 0.5 }}>Service Name</span>
                        }
                      </div>
                      <div className="item-price">
                        {currentService.price ? `$${parseFloat(currentService.price).toFixed(2)}` : 
      <                   span className="empty-text">$0.00</span>}
                      </div>
                    </MobileTemplateContent>
                  </MobileTemplateCard>

                  {/* Desktop Service Card */}
                  <DesktopItemCard theme={selectedTheme}>
                    <DeleteButton onClick={() => handleServiceDelete(service.id)}>
                      <X size={16} />
                    </DeleteButton>
                    
                    <ItemImageContainer theme={selectedTheme}>
                      <div className="image-container">
                        {service.images[service.currentImageIndex] ? (
                          <EditableImage
                            value={service.images[service.currentImageIndex]}
                            onChange={(value) => {
                              const newImages = [...service.images];
                              newImages[service.currentImageIndex] = value;
                              handleServiceUpdate(service.id, { images: newImages });
                            }}
                            theme={selectedTheme}
                            height="100%"
                            width="100%"
                          />
                        ) : (
                          <div 
                            className="placeholder"
                            onClick={() => document.getElementById(`service-image-upload-${service.id}-${service.currentImageIndex}`).click()}
                          >
                            <div style={{
                              width: '80px',
                              height: '80px',
                              borderRadius: '50%',
                              background: `${selectedTheme?.colors?.accent}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginBottom: '0.75rem'
                            }}>
                              <Store size={36} color={selectedTheme?.colors?.accent} />
                            </div>
                            <span>Add Service Photo</span>
                          </div>
                        )}
                      </div>
                      
                      <input
                        type="file"
                        id={`service-image-upload-${service.id}-${service.currentImageIndex}`}
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            validateImageFile(file, 20);
                          } catch (err) {
                            alert(err.message || 'Invalid image file.');
                            return;
                          }
                          let compressedFile;
                          try {
                            compressedFile = await compressProductImage(file);
                          } catch (err) {
                            console.warn('⚠️ Compression failed, using original file uncompressed:', err);
                            compressedFile = file;
                          }
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const newImages = [...service.images];
                            newImages[service.currentImageIndex] = {
                              file: compressedFile,
                              preview: reader.result,
                              type: compressedFile.type,
                              name: compressedFile.name
                            };
                            handleServiceUpdate(service.id, { images: newImages });
                          };
                          reader.readAsDataURL(compressedFile);
                        }}
                      />

                      {validImages.length > 0 && (
                        <>
                          <button 
                            className="carousel-arrow left"
                            onClick={() => {
                              const newIndex = ((service.currentImageIndex - 1) + 3) % 3;
                              handleServiceUpdate(service.id, { currentImageIndex: newIndex });
                            }}
                          >
                            <ChevronLeft size={16} />
                          </button>
                          <button 
                            className="carousel-arrow right"
                            onClick={() => {
                              const newIndex = (service.currentImageIndex + 1) % 3;
                              handleServiceUpdate(service.id, { currentImageIndex: newIndex });
                            }}
                          >
                            <ChevronRight size={16} />
                          </button>
                        </>
                      )}
                    </ItemImageContainer>
                    
                    <ItemContent>
                      <ItemHeader onClick={() => toggleItemExpansion(service.id)}>
                        <h4>
                          {service.name && service.name !== 'Service Name' ? 
                            service.name : 
                            <span style={{ opacity: 0.5 }}>Service Name</span>
                          }
                        </h4>
                        <ExpandButton>
                          {expandedItems.has(service.id) ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </ExpandButton>
                      </ItemHeader>
                      
                      <ItemDetails expanded={expandedItems.has(service.id)}>
                        <div className="details-content">
                          <ValidatedEditableText
                            value={service.name}
                            onChange={(value) => handleServiceUpdate(service.id, { name: value })}
                            placeholder="Service Name"
                            validationRules={VALIDATION_RULES.item.name}
                            theme={selectedTheme}
                          />
                          <ValidatedEditableText
                            value={service.price}
                            onChange={(value) => handleServiceUpdate(service.id, { price: value })}
                            placeholder="Cost"
                            validationRules={VALIDATION_RULES.item.price}
                            theme={selectedTheme}
                          />
                          <ValidatedEditableText
                            value={service.description}
                            onChange={(value) => handleServiceUpdate(service.id, { description: value })}
                            placeholder="Service Description"
                            validationRules={VALIDATION_RULES.item.description}
                            multiline
                            theme={selectedTheme}
                          />
                          <CategorySelect
  value={service.category || 'Other'}
  onChange={(e) => handleServiceUpdate(service.id, { category: e.target.value })}
  theme={selectedTheme}
>
  {SERVICE_CATEGORIES.map(category => (
    <option key={category} value={category}>
      {category}
    </option>
  ))}
</CategorySelect>
                          <div style={{ marginBottom: '1rem' }}>
                            <QuantitySelector 
                              value={parseInt(service.slots) || 1}
                              onChange={(value) => handleServiceUpdate(service.id, { slots: value })}
                              theme={selectedTheme}
                              min={0}
                              max={9999}
                            />
                          </div>
                          <AddressInput
  address={service.address || ''}
  onAddressChange={(value) => handleServiceUpdate(service.id, { 
    address: value
  })}
  onLocationSelect={(location) => {
    if (location?.coordinates) {
      handleServiceUpdate(service.id, {
        address: location.address,
        coordinates: location.coordinates
      });
    } else if (!location?.address) {
      handleServiceUpdate(service.id, {
        address: '',
        coordinates: null
      });
    }
  }}
  onMapOpen={() => handleOpenMapForSelection(service.id, true)}
/>
                        </div>
                      </ItemDetails>
                    </ItemContent>
                  </DesktopItemCard>
                </React.Fragment>
              );
            })}
          </ItemsGrid>
        </ItemsContainer>
      </div>
    )}

    {/* Mobile Edit Modal */}
    {editingItem && (
      <EditModal onClick={() => setEditingItem(null)}>
        <EditModalContent 
          theme={selectedTheme}
          onClick={(e) => e.stopPropagation()}
        >
          <ImageActionButtons theme={selectedTheme}>
            <ImageActionButton 
              className="check"
              onClick={async () => {
                const processedImages = editingItem.images.map((img) => {
                  if (typeof img === 'string') return img;
                  if (!img) return null;
                  if (img?.preview) return img.preview;
                  if (img instanceof File) return URL.createObjectURL(img);
                  return img;
                });

                const updatedItem = {
                  ...editingItem,
                  images: processedImages
                };

                if (editingItem.isService) {
                  handleServiceUpdate(editingItem.id, updatedItem);
                } else {
                  handleItemUpdate(editingItem.id, updatedItem);
                }
                setEditingItem(null);
              }}
              title="Save changes"
            >
              <Check size={20} />
            </ImageActionButton>
            <ImageActionButton 
              className="close"
              onClick={() => setEditingItem(null)}
              title="Discard changes"
            >
              <X size={20} />
            </ImageActionButton>
          </ImageActionButtons>
            
          <EditModalScrollContent theme={selectedTheme}>
            <EditModalImageSection theme={selectedTheme}>
              <ItemImageContainer theme={selectedTheme} style={{ height: '100%', minHeight: '300px' }}>
                <div className="image-container">
                  <EditableImage
                    value={editingItem.images[editingItem.currentImageIndex]}
                    onChange={(value) => {
                      const newImages = [...editingItem.images];
                      newImages[editingItem.currentImageIndex] = value;
                      setEditingItem({ ...editingItem, images: newImages });
                    }}
                    theme={selectedTheme}
                    height="100%"
                    width="100%"
                  />
                </div>
                  
                {editingItem.images.filter(Boolean).length > 1 && (
                  <>
                    <button 
                      className="carousel-arrow left"
                      onClick={() => {
                        const newIndex = ((editingItem.currentImageIndex - 1) + 3) % 3;
                        setEditingItem({ ...editingItem, currentImageIndex: newIndex });
                      }}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button 
                      className="carousel-arrow right"
                      onClick={() => {
                        const newIndex = (editingItem.currentImageIndex + 1) % 3;
                        setEditingItem({ ...editingItem, currentImageIndex: newIndex });
                      }}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </>
                )}
              </ItemImageContainer>
            </EditModalImageSection>
              
            <EditModalBody>
              <ValidatedEditableText
                value={editingItem.name}
                onChange={(value) => setEditingItem({ ...editingItem, name: value })}
                placeholder={editingItem.isService ? "Service Name" : "Item Name"}
                validationRules={VALIDATION_RULES.item.name}
                theme={selectedTheme}
              />

              <ValidatedEditableText
                value={editingItem.price}
                onChange={(value) => setEditingItem({ ...editingItem, price: value })}
                placeholder="Cost"
                validationRules={VALIDATION_RULES.item.price}
                theme={selectedTheme}
              />

              <ValidatedEditableText
                value={editingItem.description}
                onChange={(value) => setEditingItem({ ...editingItem, description: value })}
                placeholder={editingItem.isService ? "Service Description" : "Item Description"}
                validationRules={VALIDATION_RULES.item.description}
                multiline
                theme={selectedTheme}
              />

              <CategorySelect
                value={editingItem.category || 'Other'}
                onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                theme={selectedTheme}
              >
                {(editingItem.isService ? SERVICE_CATEGORIES : ITEM_CATEGORIES).map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </CategorySelect>
              
              <div style={{ marginBottom: '1rem' }}>
                <QuantitySelector 
                  value={parseInt(editingItem.isService ? editingItem.slots : editingItem.quantity) || 1}
                  onChange={(value) => setEditingItem({ 
                    ...editingItem, 
                    [editingItem.isService ? 'slots' : 'quantity']: value 
                  })}
                  theme={selectedTheme}
                  min={0}
                  max={9999}
                />
              </div>
                
              <AddressInput
  address={editingItem.address || ''}
  onAddressChange={(value) => setEditingItem({ 
    ...editingItem, 
    address: value
  })}
  onLocationSelect={(location) => {
    if (location?.coordinates) {
      setEditingItem({
        ...editingItem,
        address: location.address,
        coordinates: location.coordinates
      });
    } else if (!location?.address) {
      setEditingItem({
        ...editingItem,
        address: '',
        coordinates: null
      });
    }
  }}
  onMapOpen={() => handleOpenMapForSelection(editingItem.id, editingItem.isService)}
/>
            </EditModalBody>
          </EditModalScrollContent>
        </EditModalContent>
      </EditModal>
    )}
  </MainContent>
);

  // UPDATE the renderHomeView function in LiveShopCreation.js

const renderHomeView = () => {
  // Professional placeholder landscape images (same as ShopPage)
  

  // Get filtered items for featured section
  const featuredItems = shopData?.items?.filter(item => 
    item && 
    item.name && 
    item.name !== 'Item Name' && 
    !item.deleted
  ).slice(0, 4) || [];

  // Get item image URL
  const getItemImage = (item) => {
    if (!item?.images || item.images.length === 0) return null;
    const validImage = item.images.find(img => {
      if (typeof img === 'string') return img;
      if (img?.preview) return img.preview;
      return null;
    });
    if (typeof validImage === 'string') return validImage;
    if (validImage?.preview) return validImage.preview;
    return null;
  };

   return (
    <div style={{ 
      position: 'relative',
      paddingTop: '6rem', // CHANGED: Remove padding
      marginTop: window.innerWidth > 768 ? '0' : '0' // CHANGED: No margin
    }}>
      {/* Hero Banner Section - Should start immediately */}
      <SectionWrapper theme={selectedTheme} noPadding delay={0}>
        <style>{`
          @keyframes heroFadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}</style>
        
        <div className="hero-container" style={{
          animation: 'heroFadeIn 1.5s ease-in-out forwards',
          opacity: 0,
          padding: '0',
          background: 'transparent',
          paddingTop: '.5rem' // CHANGED: No margin at all
        }}>
          <div style={{
            minHeight: window.innerWidth <= 768 ?
              `400px` :
              '60vh',
            borderRadius: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            boxShadow: `0 0 40px ${selectedTheme?.colors?.accent}30`,
            overflow: 'hidden',
            border: 'none',
            borderTop: 'none', // CHANGED: Remove top border
            background: `linear-gradient(135deg, ${selectedTheme?.colors?.background}40 0%, transparent 100%)`
          }}>
      {/* Removed background image layer - stars now visible */}
      
      <div style={{ 
        position: 'relative', 
        zIndex: 1, 
        padding: 'clamp(1rem, 3vw, 2rem)',
        maxWidth: '900px',
        width: '100%',
        textAlign: 'center'
      }}>
        {/* Editable Brand Name */}
        <div style={{ 
          position: 'relative',
          display: 'inline-block',
          width: '100%',
          marginBottom: 'clamp(1rem, 2vw, 1.5rem)'
        }}>
          <input
            value={shopData?.name || ''}
            onChange={(e) => handleShopDataChange('name', e.target.value)}
            placeholder="-Brand Name-"
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              borderBottom: 'none',
              fontSize: window.innerWidth <= 768 ? 
                'clamp(1.5rem, 9vw, 4rem)':
                'clamp(2.5rem, 7vw, 6rem)',
              fontWeight: '900',
              margin: 0,
              lineHeight: 1.1,
              textShadow: `
                0 0 20px ${selectedTheme?.colors?.accent},
                0 0 40px ${selectedTheme?.colors?.accent}80,
                2px 2px 8px rgba(0,0,0,0.8)
              `,
              color: '#FFFFFF',
              textAlign: 'center',
              outline: 'none',
              padding: '0.25rem 0',
              caretColor: selectedTheme?.colors?.accent,
              transition: 'all 0.3s ease',
              fontFamily: selectedTheme?.fonts?.heading || 'inherit',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              opacity: 1
            }}
          />
          <style>{`
            input::placeholder {
              color: #FFFFFF !important;
              opacity: 0.9 !important;
              text-shadow: 
                0 0 15px ${selectedTheme?.colors?.accent},
                0 0 30px ${selectedTheme?.colors?.accent}60;
            }
            input::-webkit-input-placeholder {
              color: #FFFFFF !important;
              opacity: 0.9 !important;
            }
            input::-moz-placeholder {
              color: #FFFFFF !important;
              opacity: 0.9 !important;
            }
            input:-ms-input-placeholder {
              color: #FFFFFF !important;
              opacity: 0.9 !important;
            }
          `}</style>
        </div>
        
        <FadingUnderline theme={selectedTheme} /> 

        {/* Subdomain Display */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap',
          padding: '0.75rem 1rem',
          background: `${selectedTheme?.colors?.surface}30`,
          borderRadius: '8px',
          border: `1px solid ${selectedTheme?.colors?.accent}40`,
          backdropFilter: 'blur(10px)',
          margin: '0 auto',
          maxWidth: 'fit-content',
          boxShadow: `0 0 20px ${selectedTheme?.colors?.accent}20`
        }}>
          <span style={{
            fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem',
            fontWeight: '600',
            color: selectedTheme?.colors?.accent,
            fontFamily: selectedTheme?.fonts?.body,
            wordBreak: 'break-all',
            textAlign: 'center',
            textShadow: `0 0 8px ${selectedTheme?.colors?.accent}40`
          }}>
            {shopData?.name ?
              `${shopData.name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}.KalKode.Com` :
              'BrandName.KalKode.Com'
            }
          </span>
        </div>

      </div>
    </div>
  </div>
</SectionWrapper>

      {/* ── Pricing Section ── */}
      <SectionWrapper theme={selectedTheme} delay={0.15}>
        <div style={{
          background: `${selectedTheme?.colors?.surface}30`,
          borderRadius: 'clamp(12px, 2.5vw, 20px)',
          padding: 'clamp(1.5rem, 4vw, 3rem) clamp(1rem, 3vw, 2.5rem)',
          border: `1px solid ${selectedTheme?.colors?.accent}25`,
          backdropFilter: 'blur(8px)',
        }}>
          {/* Section heading */}
          <div style={{ textAlign: 'center', marginBottom: 'clamp(1.2rem, 3vw, 2rem)' }}>
            <h2 style={{ fontSize: 'clamp(1.3rem, 4vw, 2.2rem)', fontWeight: 800, color: selectedTheme?.colors?.accent, margin: 0 }}>
              Pricing
            </h2>
            <p style={{ fontSize: 'clamp(0.8rem, 2vw, 1rem)', color: `${selectedTheme?.colors?.text}99`, margin: '0.4rem 0 0', lineHeight: 1.5 }}>
              Early bird deal: One-time setup fee. No monthly charges. Upgrade anytime.
            </p>
          </div>

          {/* Plan cards — generated from billingTiers.js so pricing/limits shown
              here can never drift from what's actually enforced in ShopPage */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(140px, 20vw, 190px), 1fr))',
            gap: 'clamp(0.6rem, 2vw, 1.1rem)',
          }}>
            {PLAN_ORDER.map(key => {
              const perms = PLAN_PERMISSIONS[key];
              return {
                key,
                label: PLANS[key].label,
                price: `$${PLANS[key].price}`,
                color: PLANS[key].color,
                badge: key === 'pro' ? 'Popular' : key === 'elite' ? 'Best Value' : null,
                rows: [
                  { icon: '📦', text: `${perms.maxVisible} visible card${perms.maxVisible !== 1 ? 's' : ''}` },
                  { icon: '🗂️', text: `${perms.maxTabs} page${perms.maxTabs !== 1 ? 's' : ''}` },
                  { icon: '📋', text: 'Unlimited gig posting' },
                  perms.canPickupGigs
                    ? { icon: '✅', text: 'Pick up gigs' }
                    : { icon: '🚫', text: 'Cannot pick up gigs' },
                  ...(perms.boostedListings ? [{ icon: '🚀', text: 'Boosted listings' }] : []),
                ],
              };
            }).map(plan => {
              const isSelected = selectedPlan === plan.key;
              return (
                <div
                  key={plan.key}
                  onClick={() => setSelectedPlan(plan.key)}
                  style={{
                    borderRadius: 'clamp(10px, 2vw, 14px)',
                    border: `2px solid ${isSelected ? plan.color : 'rgba(255,255,255,0.1)'}`,
                    background: isSelected ? `${plan.color}18` : 'rgba(0,0,0,0.28)',
                    padding: 'clamp(0.75rem, 2vw, 1.1rem) clamp(0.65rem, 2vw, 1rem)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backdropFilter: 'blur(10px)',
                    boxShadow: isSelected ? `0 0 18px ${plan.color}40` : 'none',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Badge */}
                  {plan.badge && (
                    <div style={{
                      position: 'absolute', top: 0, right: 0,
                      background: plan.color, color: '#000',
                      fontSize: 'clamp(0.5rem, 1.5vw, 0.62rem)',
                      fontWeight: 800,
                      padding: '0.18rem 0.5rem',
                      borderBottomLeftRadius: '8px',
                      letterSpacing: '0.03em',
                    }}>{plan.badge}</div>
                  )}

                  {/* Plan name + selector dot */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem' }}>
                    <div style={{ width: 9, height: 9, borderRadius: '50%', border: `2px solid ${plan.color}`, background: isSelected ? plan.color : 'transparent', flexShrink: 0 }} />
                    <span style={{ fontWeight: 800, fontSize: 'clamp(0.72rem, 2.5vw, 0.92rem)', color: plan.color }}>{plan.label}</span>
                  </div>

                  {/* Price */}
                  <div style={{ fontWeight: 900, fontSize: 'clamp(1.1rem, 3.5vw, 1.5rem)', color: 'white', lineHeight: 1, marginBottom: '0.1rem' }}>{plan.price}</div>
                  <div style={{ fontSize: 'clamp(0.58rem, 1.6vw, 0.68rem)', color: `${plan.color}cc`, fontWeight: 600, marginBottom: 'clamp(0.5rem, 1.5vw, 0.75rem)' }}>one-time</div>

                  {/* Feature rows */}
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.28rem' }}>
                    {plan.rows.map((row, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.3rem', fontSize: 'clamp(0.6rem, 1.8vw, 0.72rem)', color: 'rgba(255,255,255,0.82)', lineHeight: 1.35 }}>
                        <span style={{ flexShrink: 0, fontSize: 'clamp(0.6rem, 1.8vw, 0.7rem)' }}>{row.icon}</span>
                        <span>{row.text}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Selected indicator */}
                  {isSelected && (
                    <div style={{ marginTop: '0.65rem', textAlign: 'center', fontSize: 'clamp(0.58rem, 1.6vw, 0.7rem)', color: plan.color, fontWeight: 700 }}>
                      ✓ Selected
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footnote */}
          <p style={{ textAlign: 'center', fontSize: 'clamp(0.65rem, 1.6vw, 0.75rem)', color: `${selectedTheme?.colors?.text}60`, marginTop: 'clamp(0.75rem, 2vw, 1.2rem)', margin: 'clamp(0.75rem, 2vw, 1.2rem) auto 0', maxWidth: 500 }}>
            Gig cards (jobs you need done) are always free to post on all plans. Service cards let others find and hire you as a professional.
          </p>
        </div>
      </SectionWrapper>

      {/* Mission Section */}
      <SectionWrapper theme={selectedTheme} delay={0.2}>
  <div style={{
    background: `${selectedTheme?.colors?.surface}40`,
    borderRadius: 'clamp(10px, 2.5vw, 20px)',
    padding: 'clamp(1.5rem, 5vw, 4rem) clamp(1rem, 4vw, 3rem)',
    border: `1px solid ${selectedTheme?.colors?.accent}20`,
    backdropFilter: 'blur(5px)'
  }}>
    <div style={{
      display: 'grid',
      gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1.5fr',
      gap: 'clamp(1.5rem, 4vw, 3rem)',
      alignItems: 'center'
    }}>
      {/* Image Box - Left Side */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: window.innerWidth <= 768 ? '250px' : '400px',
        borderRadius: 'clamp(8px, 2vw, 16px)',
        overflow: 'hidden',
        border: `2px solid ${selectedTheme?.colors?.accent}40`,
        boxShadow: `0 8px 24px ${selectedTheme?.colors?.accent}20`,
        order: window.innerWidth <= 768 ? 1 : 0
      }}>
        <img
          src="https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?q=80&w=2071&auto=format&fit=crop"
          alt="Mission"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
      {/* Mission Text - Right Side */}
      <div style={{ textAlign: 'center', order: window.innerWidth <= 768 ? 2 : 1 }}>
        <h2 style={{ fontSize: 'clamp(1.3rem, 5vw, 2.5rem)', color: selectedTheme?.colors?.accent, marginBottom: 'clamp(1rem, 3vw, 2rem)', fontWeight: '700' }}>
          Mission
        </h2>
        <p style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.3rem)', lineHeight: 1.6, opacity: 0.9, color: selectedTheme?.colors?.text, textAlign: 'center' }}>
          We have top quality products ready to be delivered with exceptional customer service. Our commitment to quality drives everything we do.
        </p>
      </div>
    </div>
  </div>
</SectionWrapper>

      {/* Gallery Section */}
      <SectionWrapper theme={selectedTheme} delay={0.4}>
  <div style={{ padding: 'clamp(1rem, 2vw, 2rem)', background: `${selectedTheme?.colors?.surface}30`, borderRadius: 'clamp(12px, 2vw, 16px)', border: `1px solid ${selectedTheme?.colors?.accent}30` }}>
    <h2 style={{ fontSize: 'clamp(1.3rem, 3.5vw, 2.5rem)', fontWeight: '700', marginBottom: 'clamp(1rem, 2vw, 2rem)', textAlign: 'center', color: selectedTheme?.colors?.text }}>
      Gallery
    </h2>
    <div
      style={{ position: 'relative', height: 'clamp(300px, 50vw, 500px)', borderRadius: 'clamp(10px, 2.5vw, 20px)', overflow: 'hidden', background: `${selectedTheme?.colors?.background}80`, border: `2px solid ${selectedTheme?.colors?.accent}40`, marginBottom: 'clamp(1rem, 2vw, 2rem)', cursor: 'grab', touchAction: 'pan-y pinch-zoom' }}
      onTouchStart={handleGalleryTouchStart}
      onTouchMove={handleGalleryTouchMove}
      onTouchEnd={handleGalleryTouchEnd}
    >
      {placeholderImages.map((image, index) => (
        <div key={index} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: currentGalleryImage === index ? 1 : 0, transition: 'opacity 0.5s ease-in-out', backgroundImage: `url(${image})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', pointerEvents: 'none' }} />
      ))}
      <button onClick={() => setCurrentGalleryImage((prev) => (prev - 1 + placeholderImages.length) % placeholderImages.length)} style={{ position: 'absolute', left: 'clamp(0.5rem, 2vw, 1rem)', top: '50%', transform: 'translateY(-50%)', background: `${selectedTheme?.colors?.background}CC`, border: `1px solid ${selectedTheme?.colors?.accent}40`, borderRadius: '50%', width: 'clamp(36px, 8vw, 48px)', height: 'clamp(36px, 8vw, 48px)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 3, color: selectedTheme?.colors?.text, transition: 'all 0.3s ease' }}>
        <ChevronLeft size={24} />
      </button>
      <button onClick={() => setCurrentGalleryImage((prev) => (prev + 1) % placeholderImages.length)} style={{ position: 'absolute', right: 'clamp(0.5rem, 2vw, 1rem)', top: '50%', transform: 'translateY(-50%)', background: `${selectedTheme?.colors?.background}CC`, border: `1px solid ${selectedTheme?.colors?.accent}40`, borderRadius: '50%', width: 'clamp(36px, 8vw, 48px)', height: 'clamp(36px, 8vw, 48px)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 3, color: selectedTheme?.colors?.text, transition: 'all 0.3s ease' }}>
        <ChevronRight size={24} />
      </button>
      <div style={{ position: 'absolute', bottom: 'clamp(0.75rem, 2vw, 1.5rem)', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 'clamp(0.5rem, 1.5vw, 0.75rem)', zIndex: 3 }}>
        {placeholderImages.map((_, index) => (
          <button key={index} onClick={() => setCurrentGalleryImage(index)} style={{ width: 'clamp(10px, 2.5vw, 14px)', height: 'clamp(10px, 2.5vw, 14px)', borderRadius: '50%', border: 'none', background: currentGalleryImage === index ? selectedTheme?.colors?.accent : 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: currentGalleryImage === index ? `0 0 10px ${selectedTheme?.colors?.accent}` : 'none' }} />
        ))}
      </div>
    </div>
  </div>
</SectionWrapper>

      {/* Contact Footer Section */}
      <SectionWrapper theme={selectedTheme} delay={0.6}>
        <div style={{ background: `${selectedTheme?.colors?.surface}80`, borderRadius: 'clamp(12px, 2vw, 16px)', padding: 'clamp(2rem, 4vw, 3rem)', border: `1px solid ${selectedTheme?.colors?.accent}30`, marginTop: 'clamp(2rem, 4vw, 4rem)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'clamp(2rem, 4vw, 3rem)', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h3 style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)', fontWeight: '700', color: selectedTheme?.colors?.accent, marginBottom: '0.5rem' }}>Get In Touch</h3>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                {[Instagram, Twitter, Facebook].map((Icon, i) => (
                  <div key={i} style={{ width: '36px', height: '36px', borderRadius: '50%', background: `${selectedTheme?.colors?.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.3s ease', border: `1px solid ${selectedTheme?.colors?.accent}30` }}>
                    <Icon size={18} color={selectedTheme?.colors?.accent} />
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h4 style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', fontWeight: '600', color: selectedTheme?.colors?.text, marginBottom: '0.5rem' }}>Contact Info</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[{ Icon: Mail, text: 'contact@example.com' }, { Icon: Phone, text: '(555) 123-4567' }, { Icon: MapPin, text: '123 Main Street\nCity, State 12345' }].map(({ Icon, text }, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', color: selectedTheme?.colors?.text, opacity: 0.8 }}>
                    <Icon size={18} color={selectedTheme?.colors?.accent} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span style={{ fontSize: 'clamp(0.85rem, 1.5vw, 0.95rem)', whiteSpace: 'pre-line' }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h4 style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', fontWeight: '600', color: selectedTheme?.colors?.text, marginBottom: '0.5rem' }}>Business Hours</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[{ day: 'Monday - Friday', hours: '9:00 AM - 6:00 PM' }, { day: 'Saturday', hours: '10:00 AM - 4:00 PM' }, { day: 'Sunday', hours: 'Closed' }].map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.25rem 0' }}>
                    <span style={{ fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)', color: selectedTheme?.colors?.text, opacity: 0.8 }}>{s.day}</span>
                    <span style={{ fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)', color: selectedTheme?.colors?.accent, fontWeight: '500' }}>{s.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ borderTop: `1px solid ${selectedTheme?.colors?.accent}20`, marginTop: 'clamp(2rem, 4vw, 3rem)', paddingTop: 'clamp(1rem, 2vw, 1.5rem)', textAlign: 'center' }}>
            <p style={{ fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)', color: selectedTheme?.colors?.text, opacity: 0.6, margin: 0 }}>
              © {new Date().getFullYear()} {shopData?.name || 'Your Brand'}. All rights reserved.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 'clamp(1rem, 2vw, 2rem)', marginTop: '0.75rem', flexWrap: 'wrap' }}>
              {['Privacy Policy', 'Terms of Service'].map(label => (
                <span key={label} style={{ fontSize: 'clamp(0.75rem, 1.3vw, 0.85rem)', color: selectedTheme?.colors?.text, opacity: 0.6, cursor: 'pointer' }}>{label}</span>
              ))}
            </div>
          </div>
        </div>
      </SectionWrapper>
    </div>
  );
};

  const handleWebsiteImport = async () => {
    if (!externalWebsiteUrl.trim()) return;
    setImportLoading(true);
    setImportError('');
    setImportedItems([]);
    setImportedShopMeta(null);
    setSelectedImportIds(new Set());
    try {
      const res = await fetch('/api/compress-media?action=import-shop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: externalWebsiteUrl.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Import failed');
      setImportedItems(data.items || []);
      setImportedShopMeta(data.shopMeta || null);
      setSelectedImportIds(new Set((data.items || []).map((_, i) => i)));
      if ((data.items || []).length === 0) {
        setImportError("No products found on this website. Make sure it's a public store with product listings.");
      }
    } catch (err) {
      setImportError(err.message || 'Could not reach the website.');
    } finally {
      setImportLoading(false);
    }
  };

  const handleAddImportedItems = () => {
    const toAdd = importedItems
      .filter((_, i) => selectedImportIds.has(i))
      .map((item, i) => ({
        id: `external-${Date.now()}-${i}`,
        name: item.name || 'Imported Item',
        price: item.price || '',
        description: item.description || '',
        category: 'Other',
        images: item.image ? [item.image, null, null] : [null, null, null],
        currentImageIndex: 0,
        address: '',
        coordinates: null,
        tags: [],
        quantity: item.quantity || 99,
        source: 'external',
        externalId: item.externalId || '',
        externalUrl: item.externalUrl || externalWebsiteUrl
      }));
    setShopData(prev => ({ ...prev, items: [...toAdd, ...prev.items] }));
    const meta = importedShopMeta;
    setImportedItems([]);
    setSelectedImportIds(new Set());
    setImportedShopMeta(null);
    setWebsiteExpanded(false);
    // Auto-fill shop name/description if empty
    if (meta?.name && !shopData.name) {
      setShopData(prev => ({ ...prev, name: meta.name, description: meta.description || prev.description }));
    }
  };

  const renderProfileView = () => {
  // Calculate stats
  const totalPosts = (shopData?.items?.length || 0) + (shopData?.services?.length || 0);
  
  return (
    <MainContent>
      

      {/* Profile Header Section */}
      <div style={{
        background: `${selectedTheme?.colors?.surface}30`,
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        border: `1px solid ${selectedTheme?.colors?.accent}30`,
        padding: '1rem 1.5rem 1.25rem',
        paddingTop: '4.5rem',
        marginTop: '5rem',
        marginBottom: '1rem',
        maxWidth: '935px',
        margin: '0 auto 2rem'
      }}>
  <div style={{
  display: 'flex',
  gap: '0',
  alignItems: 'center',
  flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
}}>
    {/* Profile Picture - Takes up left space */}
    <div style={{
      flex: window.innerWidth <= 768 ? '0 0 auto' : '0 0 150px',
      position: 'relative',
      marginTop: window.innerWidth <= 768 ? '3rem' : '0',
      marginBottom: window.innerWidth <= 768 ? '1.25rem' : '0'
    }}>
      <div 
        style={{
          width: window.innerWidth <= 768 ? '100px' : '150px',
          height: window.innerWidth <= 768 ? '100px' : '150px',
          borderRadius: '50%',
          overflow: 'hidden',
          background: `${selectedTheme?.colors?.background}50`,
          border: `3px solid ${selectedTheme?.colors?.accent}`,
          boxShadow: `0 0 20px ${selectedTheme?.colors?.accent}40`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          position: 'relative',
          margin: window.innerWidth <= 768 ? '0 auto' : '0'
        }}
        onClick={() => document.getElementById('profile-pic-upload').click()}
        onMouseEnter={(e) => {
          if (window.innerWidth > 768) {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = `0 0 30px ${selectedTheme?.colors?.accent}60`;
          }
        }}
        onMouseLeave={(e) => {
          if (window.innerWidth > 768) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = `0 0 20px ${selectedTheme?.colors?.accent}40`;
          }
        }}
      >
        {shopData.profile ? (
          <img 
            src={typeof shopData.profile === 'string' ? shopData.profile : shopData.profile.preview}
            alt="Profile"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
            color: selectedTheme?.colors?.accent,
            opacity: 0.5
          }}>
            <Camera size={window.innerWidth <= 768 ? 32 : 48} />
            <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>
              Add Photo
            </span>
          </div>
        )}
        
        {/* Hover overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 0.3s ease',
          borderRadius: '50%',
          pointerEvents: 'none'
        }}
        className="profile-hover-overlay"
        >
          <Camera size={24} color="white" />
        </div>
      </div>
      
      {/* Hidden file input */}
      <input
        type="file"
        id="profile-pic-upload"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          try {
            validateImageFile(file, 20);
          } catch (err) {
            alert(err.message || 'Invalid image file.');
            return;
          }
          let compressedFile;
          try {
            compressedFile = await compressProductImage(file);
          } catch (err) {
            console.warn('⚠️ Compression failed, using original file uncompressed:', err);
            compressedFile = file;
          }
          const reader = new FileReader();
          reader.onloadend = () => {
            handleShopDataChange('profile', {
              file: compressedFile,
              preview: reader.result,
              type: compressedFile.type,
              name: compressedFile.name
            });
          };
          reader.readAsDataURL(compressedFile);
        }}
      />
    </div>

    {/* Profile Info - Centered in remaining space */}
<div style={{
  flex: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: '0',
  marginLeft: window.innerWidth <= 768 ? '0' : '-55px',
  paddingRight: window.innerWidth <= 768 ? '0' : '100px',
}}>
  {/* Username */}
  <h2 style={{
    fontSize: window.innerWidth <= 768 ? '1.5rem' : '1.75rem',
    fontWeight: '300',
    color: selectedTheme?.colors?.text,
    margin: '0 0 0.85rem 0',
    fontFamily: selectedTheme?.fonts?.body
  }}>
    {shopData?.name || 'Brand Name'}
  </h2>

  {/* Stats Grid — 2×2 */}
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem', justifyItems: 'center' }}>
    {[
      { value: totalPosts,                          label: 'posts' },
      { value: 0, label: 'followers' },
      { value: 0, label: 'products ✓' },
      { value: 0, label: 'services ✓' },
    ].map(({ value, label }) => (
      <div key={label} style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '1rem', fontWeight: 700, color: selectedTheme?.colors?.text }}>{value}</div>
        <div style={{ fontSize: '0.75rem', color: selectedTheme?.colors?.text, opacity: 0.6 }}>{label}</div>
      </div>
    ))}
  </div>

    </div>
  </div>
</div>

      {/* Permits/Licenses Section */}
      <div style={{
  maxWidth: '935px',
  margin: '0 auto 2rem'
}}>
  <div style={{
    background: `${selectedTheme?.colors?.surface}30`,
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    border: `1px solid ${selectedTheme?.colors?.accent}30`,
    overflow: 'hidden',
    transition: 'all 0.3s ease'
  }}>
    {/* Collapsible Header */}
    <div
      onClick={() => setLicensesExpanded(!licensesExpanded)}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: window.innerWidth <= 768 ? '0.75rem 1rem' : '1.5rem',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'all 0.3s ease',
        background: licensesExpanded ? 'transparent' : 'transparent'
      }}
      onMouseEnter={(e) => {
        if (window.innerWidth > 768) {
          e.currentTarget.style.background = `${selectedTheme?.colors?.accent}10`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: '0.75rem'
      }}>
        <h3 style={{
          margin: 0,
          color: selectedTheme?.colors?.accent,
          fontSize: window.innerWidth <= 768 ? '0.82rem' : '1.1rem',
          fontWeight: '600'
        }}>
          📋 License & Permits
        </h3>
        <span style={{
          background: selectedTheme?.colors?.accent,
          color: 'white',
          padding: '0.25rem 0.5rem',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: '700'
        }}>
          {shopData.permits?.filter(Boolean).length || 0}
        </span>
      </div>
      
      <div style={{
        color: selectedTheme?.colors?.accent,
        transition: 'transform 0.3s ease',
        transform: licensesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
        display: 'flex',
        alignItems: 'center'
      }}>
        <ChevronDown size={20} />
      </div>
    </div>

    {/* Collapsible Content */}
    <div style={{
      maxHeight: licensesExpanded ? '2000px' : '0',
      opacity: licensesExpanded ? '1' : '0',
      overflow: 'hidden',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      <div style={{
        padding: licensesExpanded ? (window.innerWidth <= 768 ? '0 1rem 1rem 1rem' : '0 1.5rem 1.5rem 1.5rem') : '0',
        transition: 'padding 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <p style={{
          fontSize: window.innerWidth <= 768 ? '0.75rem' : '0.9rem',
          color: selectedTheme?.colors?.text,
          opacity: 0.7,
          textAlign: 'left',
          marginBottom: window.innerWidth <= 768 ? '0.75rem' : '1.5rem',
          marginTop: 0
        }}>
          Upload business permits, licenses, or certifications *optional*
        </p>

        {/* 3-Column Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? 
            'repeat(3, 1fr)' : // 3 columns on mobile/tablet
            'repeat(auto-fill, minmax(200px, 1fr))', // Auto on desktop
          gap: window.innerWidth <= 480 ? '0.5rem' : '1rem'
        }}>
          {/* Upload Boxes */}
          {[0, 1, 2].map((index) => {
            const permit = shopData.permits?.[index];
            
            return (
              <div
                key={index}
                style={{
                  aspectRatio: '1',
                  background: `${selectedTheme?.colors?.surface}20`,
                  border: permit ? 
                    `2px solid ${selectedTheme?.colors?.accent}60` : 
                    `2px dashed ${selectedTheme?.colors?.accent}30`,
                  borderRadius: window.innerWidth <= 480 ? '6px' : '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => document.getElementById(`permit-upload-${index}`).click()}
                onMouseEnter={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.borderColor = selectedTheme?.colors?.accent;
                  }
                }}
                onMouseLeave={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.borderColor = permit ? 
                      `${selectedTheme?.colors?.accent}60` : 
                      `${selectedTheme?.colors?.accent}30`;
                  }
                }}
              >
                {permit ? (
                  <>
                    <img
                      src={typeof permit === 'string' ? permit : permit.preview}
                      alt={`Permit ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {/* Delete button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newPermits = [...(shopData.permits || [])];
                        newPermits[index] = null;
                        handleShopDataChange('permits', newPermits);
                      }}
                      style={{
                        position: 'absolute',
                        top: window.innerWidth <= 480 ? '0.25rem' : '0.5rem',
                        right: window.innerWidth <= 480 ? '0.25rem' : '0.5rem',
                        background: 'rgba(0,0,0,0.7)',
                        border: `1px solid ${selectedTheme?.colors?.accent}`,
                        borderRadius: '50%',
                        width: window.innerWidth <= 480 ? '22px' : '28px',
                        height: window.innerWidth <= 480 ? '22px' : '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ff4444',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        zIndex: 2
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#ff4444';
                        e.currentTarget.style.color = 'white';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0,0,0,0.7)';
                        e.currentTarget.style.color = '#ff4444';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <X size={window.innerWidth <= 480 ? 11 : 14} />
                    </button>
                  </>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: window.innerWidth <= 480 ? '0.25rem' : '0.5rem',
                    color: selectedTheme?.colors?.accent,
                    opacity: 0.5,
                    padding: window.innerWidth <= 480 ? '0.5rem' : '1rem',
                    textAlign: 'center'
                  }}>
                    <Camera size={window.innerWidth <= 480 ? 20 : 32} />
                    <span style={{ 
                      fontSize: window.innerWidth <= 480 ? '0.65rem' : '0.75rem',
                      fontWeight: '600'
                    }}>
                      Upload {index + 1}
                    </span>
                  </div>
                )}
                
                <input
                  type="file"
                  id={`permit-upload-${index}`}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    // No compression here — these are legal documents (permits/licenses),
                    // and the product-image compression preset (small square canvas,
                    // blurred background) would wreck their legibility. Just cap the size.
                    try {
                      validateImageFile(file, 50);
                    } catch (err) {
                      alert(err.message || 'Invalid image file.');
                      return;
                    }
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const newPermits = [...(shopData.permits || Array(6).fill(null))];
                      newPermits[index] = {
                        file: file,
                        preview: reader.result,
                        type: file.type,
                        name: file.name
                      };
                      handleShopDataChange('permits', newPermits);
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </div>
</div>

      {/* Tabs Section - REMOVED CLIPS, Only show grid divider */}
      <div style={{
        maxWidth: '935px',
        margin: '0 auto'
      }}>
        <div style={{
          borderTop: `1px solid ${selectedTheme?.colors?.accent}30`,
          marginBottom: '2rem'
        }}></div>

        {/* Empty Posts Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: window.innerWidth <= 768 ? '0.25rem' : '1.75rem',
          marginBottom: '2rem'
        }}>
          {[...Array(9)].map((_, index) => (
            <div
              key={index}
              style={{
                aspectRatio: '1',
                background: `${selectedTheme?.colors?.surface}20`,
                border: `1px solid ${selectedTheme?.colors?.accent}20`,
                borderRadius: window.innerWidth <= 768 ? '4px' : '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Package 
                size={window.innerWidth <= 768 ? 24 : 40} 
                color={selectedTheme?.colors?.accent} 
                opacity={0.15} 
              />
            </div>
          ))}
        </div>

        {/* Empty State Message */}
        <div style={{
          textAlign: 'center',
          padding: '3rem 1rem',
          color: selectedTheme?.colors?.text,
          opacity: 0.6
        }}>
          <p style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>
            No posts yet
          </p>
          <p style={{ fontSize: '0.85rem' }}>
            Posts enabled after sign up
          </p>
        </div>
      </div>

      {/* Add CSS for hover effect */}
      <style>{`
        .profile-hover-overlay {
          opacity: 0 !important;
        }
        
        @media (min-width: 768px) {
          div:hover > .profile-hover-overlay {
            opacity: 1 !important;
          }
        }
      `}</style>
    </MainContent>
  );
};

  return (
  <ThemeProvider theme={selectedTheme}>
    <GlobalStyles />
    <PageContainer className="page-container">
      <StarField />
       <Header theme={shopData?.theme} $shopTab={activeTab === 'shop'}>
        {/* TOP ROW - Logo and Controls */}
        <HeaderTopRow>
          <HeaderLeft>
            <LogoImage 
              src={logo} 
              alt="KalKode Logo" 
              onClick={() => navigate('/')}
            />
            <Logo onClick={() => navigate('/')} theme={selectedTheme}>
              {shopData?.name || 'Brand Name'}
            </Logo>
          </HeaderLeft>
        
          {/* Desktop center tabs */}
          <HeaderCenter>
            <HeaderTabButton
              theme={shopData?.theme}
              active={activeTab === 'home'}
              onClick={() => setActiveTab('home')}
              title="Home"
            >
              <Home size={18} />
              <span className="tab-label">Home</span>
            </HeaderTabButton>
        
            <HeaderTabButton
              theme={shopData?.theme}
              active={activeTab === 'shop'}
              onClick={() => setActiveTab('shop')}
              title="Shop"
            >
              <Store size={18} />
              <span className="tab-label">Shop</span>
            </HeaderTabButton>
        
            <HeaderTabButton
              theme={shopData?.theme}
              active={activeTab === 'profile'}
              onClick={() => setActiveTab('profile')}
              title="Profile"
            >
              <Users size={18} />
              <span className="tab-label">Profile</span>
            </HeaderTabButton>
          </HeaderCenter>
        
          <HeaderRight>
            <HeaderButton
              onClick={refreshTheme}
              theme={shopData?.theme}
              className={isRefreshing ? "spinning" : ""}
              title="Random theme"
            >
              <RefreshCw size={20} />
            </HeaderButton>
      
            <HeaderButton
              onClick={togglePinStyle}
              theme={shopData?.theme}
              className={isPinned ? "pinned" : ""}
              title={isPinned ? "Unpin theme" : "Pin theme"}
            >
              <Pin size={20} fill={isPinned ? "currentColor" : "none"} />
            </HeaderButton>
          </HeaderRight>
        </HeaderTopRow>

        {/* BOTTOM ROW - Mobile Navigation Tabs */}
        <HeaderBottomRow theme={shopData?.theme} $shopTab={activeTab === 'shop'}>
          <HeaderTabButton
            theme={shopData?.theme}
            active={activeTab === 'home'}
            onClick={() => setActiveTab('home')}
            title="Home"
          >
            <Home size={16} />
            <span className="tab-label">Home</span>
          </HeaderTabButton>
      
          <HeaderTabButton
            theme={shopData?.theme}
            active={activeTab === 'shop'}
            onClick={() => setActiveTab('shop')}
            title="Shop"
          >
            <Store size={16} />
            <span className="tab-label">Shop</span>
          </HeaderTabButton>
      
          <HeaderTabButton
            theme={shopData?.theme}
            active={activeTab === 'profile'}
            onClick={() => setActiveTab('profile')}
            title="Profile"
          >
            <Users size={16} />
            <span className="tab-label">Profile</span>
          </HeaderTabButton>
        </HeaderBottomRow>
      </Header>

      {/* Tab Content with Swipe Support */}
        <div 
  style={{ 
    position: 'relative',
    minHeight: '100vh'
  }}
>
          <TabContentContainer active={activeTab === 'home'}>
  <MainContent noTopPadding={activeTab === 'home'}>
    {renderHomeView()}
  </MainContent>
</TabContentContainer>

<TabContentContainer active={activeTab === 'shop'}>
  <MainContent noTopPadding={activeTab === 'shop'}>
    {renderShopView()}
  </MainContent>
</TabContentContainer>

<TabContentContainer active={activeTab === 'profile'}>
  <MainContent noTopPadding={activeTab === 'profile'}>
    {renderProfileView()}
  </MainContent>
</TabContentContainer>
        </div>

        {/* Save Button */}
        <SaveButtonContainer isHidden={!!editingItem}>
          <ActionButton
            onClick={handleSave}
            disabled={saving}
            theme={selectedTheme}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            {saving ? 'Signing in…' : (
              <>
                <img src="https://cdn.cdnlogo.com/logos/g/35/google-icon.svg" alt="" style={{ width: 18, height: 18, flexShrink: 0 }} />
                {`${PLANS[selectedPlan]?.label || 'Free'} Plan · $${PLANS[selectedPlan]?.price ?? 0}`}
              </>
            )}
          </ActionButton>
        </SaveButtonContainer>

      {/* TreasureMap - unchanged */}
      {mapOpen && userLocation && (
        <TreasureMap
          isOpen={mapOpen}
          onClose={() => {
            setMapOpen(false);
            setSelectingLocationFor(null);
          }}
          items={[]}
          services={[]}
          userLocation={userLocation}
          theme={selectedTheme}
          selectionMode={true}
          onLocationSelect={handleMapLocationSelect}
          onRequestLocation={handleRequestLocation}
        />
      )}
    </PageContainer>
  </ThemeProvider>
);
};

export default LiveShopCreation;